import { Spine } from "@esotericsoftware/spine-pixi-v7";
import { Settings } from "../../../settings";
import * as PIXI from "pixi.js";
import anime from "animejs/lib/anime.es.js";
import TickerTimeout from "../../../utils/TickerTimeout";
import {
  FEATURES_CONFIG,
  WIN_TYPE_THRESHOLDS,
  ANIMATED_WIN_TYPES,
} from "../../../featuresConfig";
import { PixiSettings } from "../../../pixiSettings";
import { FONT_STYLES, scaleStyle, WIN_TEXT_SCALE } from "../../../fontStyles";
import SpineAtlasUploader from "../../../utils/SpineAtlasUploader";
import {
  setWinAnimationsPlaying,
  setSymbolPopupBlocked,
  setWinAnimationCounterFinished,
} from "../../../State/actions";
import { syncGameState, updateDisplayedBalance } from "../../../State/Slices/gameSettingsSlice";
import { applyReplayWin } from "../../../State/Slices/replaySlice";
import store from "../../../State/Store/store";
import { formatToCustomLocale } from "../../../utilities";
import { SOUND_NAME, MUSIC_FADE_MS, SFX_CUT_FADE_MS } from "../../../sounds";
import { soundManager } from "../../../SoundManager";
import { HowlCache } from "../../../State/Slices/soundsSlice";
import { handleShouldSyncBalanceOnWinClose } from "../../../service";



// The slot the "XX X BET" value is pinned to. win_celebration exports it as
// `txt_slot_xbet_value`; Zeus called it `txt_slot_multiplier`. Tried in order,
// first hit wins — a rename must not silently drop the value from the screen,
// which is exactly what happened when only the old name was checked.
const MULTIPLIER_SLOT_NAMES = ["txt_slot_xbet_value", "txt_slot_multiplier"];

// Background music -> win_music handover at the start of a win celebration.
// Deliberately near-instant: the old 500ms out + 500ms in put win_music at
// full only at t=1000ms, so the counter — which starts at t=0 — was the first
// thing heard. The background is out and win_music is up before the first
// banner lands.
const WIN_MUSIC_CROSSFADE_MS = MUSIC_FADE_MS;

// A banner that is CUT SHORT by a skip fades instead of stopping dead. Short
// enough that the next banner is not delayed, long enough that the cut is not
// a click. Only skips use it — a banner that ends naturally is untouched.
const BANNER_CUT_FADE_MS = SFX_CUT_FADE_MS;

// ── How fast the SKIPPED-TO banner arrives ──
//
// There is ONE `in` clip (1.000s) and the tiers are SKINS of it, so every
// banner enters on the same animation. That matters because `in` KEYS the xBet
// slot from alpha 00 to ff at its last frame — the value is authored to be
// invisible until the clip ends, and no amount of fading on our side can show
// it sooner.
//
// So the two skip paths were never equally fast, and it was nothing to do with
// the artwork:
//
//   already on the final tier   `in` is ALREADY part-played, so only its
//     (a plain big win)         remainder is left. The xBet arrives quickly.
//   skipped up a tier chain     `in` RESTARTS from zero for the tier being
//     (mega / super mega)       skipped to, so the xBet is a full second late,
//                               every time, whenever the press happened.
//
// Dropping `in` on a skip closes the gap and was tried and REVERTED — what
// enters is the final tier, which the player has not seen, and without it the
// top banner appears out of nowhere. See the note at the setAnimation below.
//
// So the arrival is kept and PLAYED FASTER. A player who skipped has asked to
// hurry; the banner still arrives, in half the time, which puts the chain case
// in the same range as the already-there case instead of a fixed second behind.
const SKIP_IN_TIME_SCALE = 2;

// ── The EXIT crossfade, and why it is not MUSIC_FADE_MS ──
//
// Every other fade in this game is deliberately near-instant. This one is not,
// because it is not covering a cut — it is covering the closing ANIMATION.
//
// The banners and the counter stop at once on a skip, which is right: the
// player asked for them to go. The music is the only thing still carrying the
// moment while the spine plays its `out`, and cutting it on the same beat left
// the whole close running in silence.
//
// So the length is READ OFF THE OUT CLIP rather than guessed, and the
// background music fades in across the same window. The two cross: win music
// leaves as the animation leaves, the base track is already up underneath it,
// and there is no silent gap between them at any point.
//
// The floor matters for the no-`out` path, where the close is instant and
// there is no clip to measure — the music still gets a tail of its own.
const WIN_MUSIC_OUTRO_FADE_MIN_MS = 400;


// ============================================================================
// WIN ANIMATION CONFIGURATION
// Fine-tune spine scale, position offset, and text sizes per device/orientation
// ============================================================================

// ── The win_celebration sizing table ──
//
// This table MUST stay identical, value for value, to the three free-spin
// popups that draw the same spine: FreeSpinTotalWin, FreeSpinMaxWin and
// FreeSpinZeroWin. They are the same artwork wearing a different skin, so any
// difference here shows up as the same picture appearing at two sizes
// depending on which one opened.
//
// `offset` is a multiple of symbolHeight, pushed UP from the screen centre.
// `buttonYOffset` lifts the tap word, in SPINE UNITS. Negative is up.
//
// Every popup puts the tap word on the SAME screen line. This spine's tap bone
// sits at -480.24 and lifts 43.66, landing on spine y -436.58 — the offset
// that gives it the same gap from the value that the xbet has. The two dialog
// spines start from -471.38 and so lift 34.80 to reach the same line.
// const DEVICE_MULTIPLIERS_MOBILE_SMALL_PORTRAIT = { scale: 1.0, offset: 0.0, buttonYOffset: 0.0 };
const DEVICE_MULTIPLIERS_MOBILE_SMALL_PORTRAIT = { scale: 1.2, offset: 0.0, buttonYOffset: 0.0 }; // 1.2 on every device (Vladimir, 2026-09-30)
// const DEVICE_MULTIPLIERS_MOBILE_SMALL_LANDSCAPE = { scale: 1.0, offset: 0.0, buttonYOffset: -43.66 };
const DEVICE_MULTIPLIERS_MOBILE_SMALL_LANDSCAPE = { scale: 1.2, offset: 0.0, buttonYOffset: -43.66 }; // 1.2 on every device (Vladimir, 2026-09-30)
// const DEVICE_MULTIPLIERS_MOBILE_PORTRAIT = { scale: 1.0, offset: 0.0, buttonYOffset: 0.0 };
const DEVICE_MULTIPLIERS_MOBILE_PORTRAIT = { scale: 1.2, offset: 0.0, buttonYOffset: 0.0 }; // 1.2 on every device (Vladimir, 2026-09-30)
// Natural size runs too large on a standard landscape phone.
// const DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE = { scale: 0.9, offset: 0.0, buttonYOffset: -43.66 };
// const DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE = { scale: 1.0, offset: 0.0, buttonYOffset: -43.66 }; // 1.0 everywhere (Vladimir, 2026-09-30)
const DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE = { scale: 1.2, offset: 0.0, buttonYOffset: -43.66 }; // 1.2 on every device (Vladimir, 2026-09-30)
// const DEVICE_MULTIPLIERS_TABLET_PORTRAIT = { scale: 1.0, offset: 0.0, buttonYOffset: 0.0 };
const DEVICE_MULTIPLIERS_TABLET_PORTRAIT = { scale: 1.2, offset: 0.0, buttonYOffset: 0.0 }; // 1.2 on every device (Vladimir, 2026-09-30)
// const DEVICE_MULTIPLIERS_TABLET_LANDSCAPE = { scale: 1.0, offset: 0.0, buttonYOffset: 0.0 };
const DEVICE_MULTIPLIERS_TABLET_LANDSCAPE = { scale: 1.2, offset: 0.0, buttonYOffset: 0.0 }; // 1.2 on every device (Vladimir, 2026-09-30)
// Desktop takes the landscape-phone treatment: same trim, same tap-word lift.
// const DEVICE_MULTIPLIERS_DESKTOP = { scale: 0.9, offset: 0.0, buttonYOffset: -43.66 };
// const DEVICE_MULTIPLIERS_DESKTOP = { scale: 1.0, offset: 0.0, buttonYOffset: -43.66 }; // 1.0 everywhere (Vladimir, 2026-09-30)
const DEVICE_MULTIPLIERS_DESKTOP = { scale: 1.2, offset: 0.0, buttonYOffset: -43.66 }; // 1.2 on every device (Vladimir, 2026-09-30)


/**
 * WinAnimationSecondaryController - Manages progressive win celebration animations (secondary theme)
 *
 * Simplified variant for Zeus/Dmon theme:
 * - Counter stays permanently attached to txt_slot_value slot (no detach/reattach)
 * - Tier transitions swap animations directly (no hide→reset→show dance)
 * - Skin switching ready (skeleton.setSkin) for future artist skins
 *
 * @class
 */
class WinAnimationSecondaryController {
  /**
   * Creates a new WinAnimationSecondaryController instance
   *
   * @param {PIXI.Container} container - Parent container for spine animations
   * @param {Object} dimensionsContext - Device dimensions and context
   * @param {Object} overlayRef - Reference to background overlay
   * @param {Function} dispatch - Redux dispatch function
   * @param {number} symbolWidth - Width of game symbols for scaling
   * @param {number} symbolHeight - Height of game symbols for scaling
   * @param {Function} t - Translation function from i18next
   */
  constructor(
    container,
    dimensionsContext,
    overlayRef,
    dispatch,
    symbolWidth,
    symbolHeight,
    t,
  ) {
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.overlayRef = overlayRef;
    this.dispatch = dispatch;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.t = t;

    // Extract dimensions object for backward compatibility
    this.dimensions = dimensionsContext.dimensions;

    // Spine instance
    this.winSpine = null;
    this.isVisible = false;
    this.isPlaying = false;
    this.isWaitingForClick = false;
    this.initialized = false;

    // Counter attached to spine bone
    this.counterContainer = null;
    this._charTexts = [];
    this.currentCounterValue = 0;
    this.counterAnimationRef = null;

    // Multiplier text attached to spine bone ("32 X BET")
    this.multiplierContainer = null;
    this.multiplierText = null;
    // ── The xBet only ever appears on the final IDLE ──
    //
    // `switch` is the fast skin swap between tiers; the xBet belongs to the
    // idle that follows it, on the tier the player has actually arrived at.
    // Only skip can put the two out of step — it stops the counter wherever
    // it lands, often mid-switch. So the reveal is a parked request that a
    // per-frame check releases. See _requestMultiplier / _pollMultiplierGate.
    this._multiplierRequest = null; // null | "multiplier" | "withButton"
    this._multiplierGateFn = null; // ticker fn while a request is parked

    // Button attached to spine bone
    this.buttonContainer = null;
    this.buttonText = null;
    // Proxy set by the React shell — routes taps on the "TAP TO CONTINUE"
    // text through the same fullscreen+sound handling as the full-screen overlay.
    this._clickHandler = null;

    // Timeouts and cleanup
    this.currentTimeouts = [];
    this.emergencyRecoveryTimeout = null;
    this.autoCloseTimeout = null;
    this.skipProtectionTimeout = null;
    this.debounceTimeout = null;

    // Animation state
    this.animationQueue = [];
    this.currentAnimationIndex = 0;
    this.finalWinType = null;
    this.lastWinAmount = null; // Prevent restart for same win

    this.currentAnimationPhase = null;
    this.phaseStartTime = 0;
    this.phaseProgress = 0;

    // User interaction state
    this.hasHandledSkipClick = false;
    this.hasHandledCloseClick = false;
    this.isHandlingUserClick = false;
    this.canSkipAnimation = false;
    this.isClosing = false;
    this.isFinishing = false;
    this.counterJumpedToFinal = false;
    // The slot the xBet is pinned to, resolved once — see _findMultiplierSlotName.
    this._multiplierSlotName = null;
    // "XX X BET" is already on screen for this celebration — the latch
    // of the one place that draws it. See _fadeInMultiplier.
    this._multiplierRevealed = false;
    this.counterDetached = false; // Track whether counter is detached from spine slot
    this.detachedScaleX = 1; // Scale factor when counter is detached
    this.detachedScaleY = 1;

    // Background music state tracking
    this.backgroundMusicWasPlaying = false;
    this.backgroundMusicType = null; // Track which background music was playing (background_music or free_spin_background_music)

    // Internal caches for performance optimization
    this._deviceMultipliersCache = null;
    this._availableAnimationsCache = null;
    this._animationProgressionCache = null;
    this._lastDevice = null;
    this._lastOrientation = null;
    this._lastIsMobileSmall = null;

    this.initializeWinSpine();
  }

  /**
   * Apply proportional scaling and centering to spine animation
   * Scales based on symbol size and device type, positions relative to screen center
   */
  applyScaleAndCenter() {
    if (!this.winSpine || !this.dimensions) return;

    // Responsive: the celebration scales with the board, so it holds its
    // proportion on every screen. The per-device multiplier is a tweak ON TOP
    // of that and is 1.0 everywhere — natural size until proven otherwise.
    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    let scale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));

    const { scale: deviceScaleMultiplier, offset: deviceOffsetMultiplier } =
      this.getDeviceMultipliers();
    scale *= deviceScaleMultiplier;

    this.winSpine.scale.set(scale);

    // The parent container sits at the screen centre, so 0,0 IS dead centre.
    // Every offset multiplier is 0.0, so y resolves to 0.
    this.winSpine.x = 0;
    this.winSpine.y = -(this.symbolHeight || 0) * deviceOffsetMultiplier;

    // Update detached counter position to match new spine transform
    this.syncDetachedCounter();
  }

  /**
   * Get device-specific scale and offset multipliers for spine animations
   * Returns both scale and offset values from configuration constants
   * Cached for performance - invalidated when device/orientation changes
   */
  getDeviceMultipliers() {
    // Resolved exactly as the three free-spin popups on this spine resolve it,
    // isMobileSmall included. Without that flag a small phone fell through to
    // the desktop row, which was harmless while every row read 1.0 and is not
    // any more.
    const { device, orientation, isMobileSmall } = this.dimensionsContext || {};

    // Check cache validity
    if (
      this._deviceMultipliersCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation &&
      this._lastIsMobileSmall === isMobileSmall
    ) {
      return this._deviceMultipliersCache;
    }

    const isPortrait = orientation === PixiSettings.ORIENTATION.PORTRAIT;
    let multipliers;
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (isMobileSmall) {
        multipliers = isPortrait
          ? DEVICE_MULTIPLIERS_MOBILE_SMALL_PORTRAIT
          : DEVICE_MULTIPLIERS_MOBILE_SMALL_LANDSCAPE;
      } else {
        multipliers = isPortrait
          ? DEVICE_MULTIPLIERS_MOBILE_PORTRAIT
          : DEVICE_MULTIPLIERS_MOBILE_LANDSCAPE;
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      multipliers = isPortrait
        ? DEVICE_MULTIPLIERS_TABLET_PORTRAIT
        : DEVICE_MULTIPLIERS_TABLET_LANDSCAPE;
    } else {
      multipliers = DEVICE_MULTIPLIERS_DESKTOP;
    }

    // Cache result
    this._deviceMultipliersCache = multipliers;
    this._lastIsMobileSmall = isMobileSmall;
    this._lastDevice = device;
    this._lastOrientation = orientation;

    return multipliers;
  }



  /**
   * Update dimensions and symbol sizes, then recalculate scale and position
   */
  updateDimensions(dimensionsContext, symbolWidth, symbolHeight) {
    // Check if any values actually changed to avoid unnecessary recalculations
    const oldDimensions = this.dimensionsContext?.dimensions;
    const newDimensions = dimensionsContext?.dimensions;
    const oldDevice = this.dimensionsContext?.device;
    const newDevice = dimensionsContext?.device;

    const dimensionsChanged =
      !oldDimensions ||
      oldDimensions.width !== newDimensions?.width ||
      oldDimensions.height !== newDimensions?.height ||
      this.dimensionsContext?.device !== dimensionsContext?.device ||
      this.dimensionsContext?.orientation !== dimensionsContext?.orientation;

    const symbolHeightsChanged =
      this.symbolWidth !== symbolWidth || this.symbolHeight !== symbolHeight;
    const deviceChanged = oldDevice !== newDevice;

    // Only proceed if something actually changed - this prevents unnecessary updates during animation start
    if (!dimensionsChanged && !symbolHeightsChanged) {
      return;
    }

    this.dimensionsContext = dimensionsContext;
    this.dimensions = dimensionsContext.dimensions; // Update the extracted dimensions object
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;

    // Invalidate caches when device/orientation changes
    if (
      deviceChanged ||
      this._lastDevice !== dimensionsContext.device ||
      this._lastOrientation !== dimensionsContext.orientation ||
      this._lastIsMobileSmall !== dimensionsContext.isMobileSmall
    ) {
      this._deviceMultipliersCache = null;
      this._lastDevice = dimensionsContext.device;
      this._lastOrientation = dimensionsContext.orientation;
      this._lastIsMobileSmall = dimensionsContext.isMobileSmall;
      // The tap word's lift is per-device, so it has to be re-read here —
      // rotating the phone changes which row applies.
      this.applyButtonOffset();
    }


    // CRITICAL: Always update spine positioning whenever dimensions change, even when hidden
    // This ensures the spine is always positioned correctly, preventing jumps on first appearance
    // Similar to FreeSpinIntroDialogController which maintains positioning at all times
    if (
      this.winSpine &&
      this.dimensions &&
      (dimensionsChanged || symbolHeightsChanged)
    ) {
      // Ensure we have valid symbolHeight before positioning
      if (this.symbolHeight && this.symbolHeight > 0) {
        this.applyScaleAndCenter();
      }
    }

    // Update counter display if dimensions changed (font size needs adjustment based on spine scale)
    if (dimensionsChanged || symbolHeightsChanged) {
      if (this.counterContainer && this.currentCounterValue !== undefined) {
        // Font size is now based on spine scale, so update when spine is rescaled
        this.updateCounterDisplay(this.currentCounterValue);
      }
    }
  }

  /**
   * Clear all tracked timeouts and timers
   *
   * Clears animation timeouts, auto-close timers, skip protection timers,
   * and emergency recovery timers. Prevents memory leaks and ensures
   * clean state when animations are interrupted.
   */
  clearAllTimeouts() {
    for (const t of this.currentTimeouts) {
      if (t && typeof t.clear === "function") {
        t.clear();
      }
    }
    this.currentTimeouts = [];

    // Clear skip protection timeout
    if (this.skipProtectionTimeout) {
      this.skipProtectionTimeout.clear();
      this.skipProtectionTimeout = null;
    }

    // Clear emergency recovery timeout
    if (this.emergencyRecoveryTimeout) {
      this.emergencyRecoveryTimeout.clear();
      this.emergencyRecoveryTimeout = null;
    }

    // Clear auto-close timeout
    if (this.autoCloseTimeout) {
      this.autoCloseTimeout.clear();
      this.autoCloseTimeout = null;
    }

    // Clear debounce timeout
    if (this.debounceTimeout) {
      this.debounceTimeout.clear();
      this.debounceTimeout = null;
    }
  }

  /**
   * Clear all resources (just timeouts now, anime fades handle themselves)
   */
  clearAllResources() {
    this.clearAllTimeouts();
  }

  /**
   * Initialize the win celebration spine
   */
  initializeWinSpine() {
    try {
      this.winSpine = this.createWinSpine();

      if (!this.winSpine) {
        return;
      }

      // Simple validation
      if (!this.winSpine.skeleton || !this.winSpine.state) {
        return;
      }

      // Add to container first
      this.container.addChild(this.winSpine);

      // Set initial properties
      // ── Hidden at alpha 0, not 0.001 ──
      //
      // 0.001 still RENDERS: PIXI skips a subtree only at `worldAlpha <= 0`. The
      // near-zero was there to keep PIXI's texture GC from unloading this spine's
      // atlas after 60 idle seconds — and that sweep is now off (see Stage.js), so
      // the trick has nothing left to buy. At 0 the whole subtree is skipped, and
      // any fade that raises alpha still brings it back exactly as before.
      this.winSpine.alpha = 0; // Hidden initially (use alpha not visible)

      // Apply initial position and scale (only if dimensions are ready)
      // If dimensions aren't ready yet, updateDimensions will position it when they become available
      if (this.symbolHeight && this.symbolHeight > 0 && this.dimensions) {
        this.applyScaleAndCenter();
      }

      this.initialized = true;

      // Configure animation mixing (crossfade) for smooth transitions
      // Spine interpolates bone positions between animations over the mix duration
      const stateData = this.winSpine.state.data;
      stateData.setMix("idle", "out", 0.25);  // 250ms blend: IDLE → OUT (closing)
      stateData.setMix("in", "idle", 0.15);   // 150ms blend: IN → IDLE (settling)
      stateData.setMix("idle", "in", 0.2);    // 200ms blend: IDLE → IN (tier transition BW→MW→SMW)
      stateData.setMix("out", "in", 0.2);     // 200ms blend: OUT → IN (skip to final tier)

      // Invalidate animations cache when spine is reinitialized
      this._availableAnimationsCache = null;

      // Initialize spine structure to compile shaders during loading
      this.initializeSpineStructure();

      // Force-upload all win_celebration atlas page textures to GPU
      SpineAtlasUploader.upload("win_celebration");

      // Initialize counter attached to spine bone
      this.initializeCounter();

      // Initialize multiplier text attached to spine bone
      this.initializeMultiplier();

      // Initialize button attached to spine bone
      this.initializeButton();
    } catch (error) {
      // Silent fail - spine initialization failed
    }
  }

  /**
   * Create win spine from loaded assets
   */
  createWinSpine() {
    try {
      // Check if assets are loaded
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "win_celebration-skeleton";
      const atlasAlias = "win_celebration-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      const winSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!winSpine || !winSpine.skeleton || !winSpine.state) {
        return null;
      }

      if (!winSpine.skeleton.data || !winSpine.skeleton.data.animations) {
        return null;
      }

      return winSpine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Initialize skeleton by setting an animation to populate spine structure
   * Minimal requirement: set one animation + update once
   */
  initializeSpineStructure() {
    if (!this.winSpine || !this.winSpine.state) {
      return;
    }

    try {
      // Set any animation to force spine to load bones/slots/attachments
      // Just pick the first available animation (usually big_win_in)
      const animations = this.getAvailableAnimations();
      if (animations.length > 0) {
        if (this.winSpine) this.winSpine.autoUpdate = true;
        this.winSpine.state.setAnimation(0, animations[0], false);
        this.winSpine.state.update(0.016); // Update one frame
        // ── Spine 4.2 REQUIRES an argument here, and this THREW ──
        //
        // The bare call raises, the catch below swallowed it, and the two lines
        // after it never ran. So the largest rig in the game — 244 bones, 248
        // slots — was left with `autoUpdate` still true from the line above and
        // solved its whole hierarchy on every frame from BOOT, for the entire
        // session, while `alpha = 0` (set at build) kept it invisible.
        //
        // The perf probe is what found it: `244b/248s x1` sat in the HIDDEN
        // column from the first sample, before a single spin.
        //
        // Same guarded shape the other eleven controllers use.
        if (this.winSpine.skeleton.physics !== undefined) {
          this.winSpine.skeleton.updateWorldTransform(2);
        } else {
          this.winSpine.skeleton.updateWorldTransform(0);
        }
      }

      this.winSpine.alpha = 0;
      if (this.winSpine) this.winSpine.autoUpdate = false;
    } catch (error) {
      // A warm-up that cannot run is not worth stopping boot for — but it must
      // not leave the skeleton ticking, which is what the bare call above did.
      if (this.winSpine) this.winSpine.autoUpdate = false;
    }
  }

  /**
   * Get available animations from the spine
   * Cached for performance - invalidated when spine is reinitialized
   */
  getAvailableAnimations() {
    if (!this.winSpine?.skeleton?.data?.animations) return [];

    // Check cache validity
    if (this._availableAnimationsCache !== null) {
      return this._availableAnimationsCache;
    }

    // Calculate and cache
    const animations = this.winSpine.skeleton.data.animations.map(
      (anim) => anim.name,
    );
    this._availableAnimationsCache = animations;

    return animations;
  }

  /**
   * Initialize counter and attach to spine slot "txt_slot_value"
   *
   * The counter is attached to a spine slot, so Spine automatically handles
   * position, rotation, and scale updates. No manual transform updates needed.
   * The counter only manages its own text content and visibility.
   */
  initializeCounter() {
    if (!this.winSpine || !this.winSpine.skeleton) {
      return;
    }

    // Check if slot exists in spine
    const counterSlot = this.winSpine.skeleton.findSlot("txt_slot_value");
    if (!counterSlot) {
      return;
    }

    // Create counter container and attach to slot
    this.counterContainer = new PIXI.Container();
    this.counterContainer.name = "win_counter_attached_to_slot";
    this.counterContainer.alpha = 0;
    if (this.counterContainer) this.counterContainer.autoUpdate = false;

    // Use Zeus-themed counter style — pixel properties scale proportionally with fontSize
    const counterBaseStyle = FONT_STYLES.winCelebrationCounter;
    this._counterBaseStyle = counterBaseStyle;
    this._counterStyle = scaleStyle(
      counterBaseStyle,
      Math.round(counterBaseStyle.fontSize * WIN_TEXT_SCALE),
    );

    // Measure per-character advance width accurately:
    // "00".width / 2 gives the true advance width (shared padding cancels out)
    const measureStyle = new PIXI.TextStyle({
      fontFamily: this._counterStyle.fontFamily,
      fontSize: this._counterStyle.fontSize,
      fontWeight: this._counterStyle.fontWeight || "normal",
    });
    const CHAR_PADDING = 4; // px breathing room between characters
    const doubleProbe = new PIXI.Text("00", measureStyle);
    this._digitCellWidth = (doubleProbe.width / 2) + CHAR_PADDING;
    doubleProbe.destroy();
    const sepProbe = new PIXI.Text(",,", measureStyle);
    this._separatorCellWidth = (sepProbe.width / 2) + CHAR_PADDING;
    sepProbe.destroy();

    // Per-character text objects will be created dynamically
    this._charTexts = [];

    // Attach to slot — Spine auto-updates position/rotation/scale
    this.winSpine.addSlotObject("txt_slot_value", this.counterContainer);

    // Cache the bone for overflow checks
    const bone = counterSlot.bone;
    this._counterBoneWidth = bone ? bone.data.length || 400 : 400;

    // Initialize with zero
    this.updateCounterDisplay(0);
  }

  /**
   * First multiplier slot name that exists on this skeleton.
   */
  _findMultiplierSlotName() {
    const skeleton = this.winSpine?.skeleton;
    if (!skeleton) return null;
    for (const name of MULTIPLIER_SLOT_NAMES) {
      // Kept, not just returned: the reveal gate reads this slot's live alpha
      // every frame to know whether the value is even being drawn yet. See
      // _pollMultiplierGate.
      if (skeleton.findSlot(name)) {
        this._multiplierSlotName = name;
        return name;
      }
    }
    return null;
  }

  /**
   * Initialize the "XX X BET" text and pin it to the skeleton's value slot.
   * Shown once the counter finishes — same font as the counter, 60% size.
   */
  initializeMultiplier() {
    if (!this.winSpine || !this.winSpine.skeleton) return;

    const multiplierSlotName = this._findMultiplierSlotName();
    if (!multiplierSlotName) {
      console.warn(
        "[WinAnimation] No multiplier slot found:",
        MULTIPLIER_SLOT_NAMES,
      );
      return;
    }
    const multiplierSlot = this.winSpine.skeleton.findSlot(multiplierSlotName);

    this.multiplierContainer = new PIXI.Container();
    this.multiplierContainer.name = "win_multiplier_attached_to_slot";

    // Its own style — the xBet text is not a smaller counter. It carries a
    // different shadow and stroke, so its size comes from its own JSON.
    const xbetBaseStyle = FONT_STYLES.winCelebrationXBet;
    this.multiplierText = new PIXI.Text(
      "",
      new PIXI.TextStyle(
        scaleStyle(
          xbetBaseStyle,
          Math.round(xbetBaseStyle.fontSize * WIN_TEXT_SCALE),
        ),
      ),
    );
    this.multiplierText.anchor.set(0.5);
    this.multiplierText.alpha = 0;
    this.multiplierText.visible = false;

    this.multiplierContainer.addChild(this.multiplierText);
    this.winSpine.addSlotObject(multiplierSlotName, this.multiplierContainer);

    // Cache bone width for overflow
    const bone = multiplierSlot.bone;
    this._multiplierBoneWidth = bone ? bone.data.length || 400 : 400;

    // Multiplier stays hidden until counter finishes — no counting animation
  }

  /**
   * Update multiplier display with a value.
   * Shows "XX X BET" where XX = floor(value / betAmount).
   *
   * @param {number} value - The value to display
   */
  updateMultiplierDisplay(value) {
    if (!this.multiplierText || !this.betAmount || this.betAmount <= 0) return;

    const multiplier = Math.floor(value / this.betAmount);
    this.multiplierText.text = `${multiplier} X BET`;

    // Multiplier always lives in slot — only use bone-local overflow
    this.multiplierContainer.scale.set(1);
    const maxWidth = this._multiplierBoneWidth * 0.9;
    if (this.multiplierText.width > maxWidth && maxWidth > 0) {
      this.multiplierContainer.scale.set(maxWidth / this.multiplierText.width);
    }
  }

  /**
   * Update counter display with new value.
   * Uses per-character fixed-width cells so digits don't cause horizontal jitter.
   * Each character gets its own PIXI.Text centered in a cell of equal width.
   *
   * @param {number} value - The value to display
   */
  updateCounterDisplay(value) {
    if (!this.counterContainer) return;

    const formattedText = formatToCustomLocale(Math.floor(value));
    const chars = formattedText.split("");

    // Calculate total width using fixed cell widths
    let totalWidth = 0;
    const cellWidths = chars.map((ch) => {
      const isDigit = ch >= "0" && ch <= "9";
      const w = isDigit ? this._digitCellWidth : this._separatorCellWidth;
      totalWidth += w;
      return w;
    });

    // Create/reuse PIXI.Text objects for each character
    //
    // `_counterStyle`, NOT the base style. This is the style the digits are
    // actually DRAWN with, while _digitCellWidth was measured from the scaled
    // one — build these from the base and the counter renders at the authored
    // size inside cells laid out for a larger one, so WIN_TEXT_SCALE appears to
    // do nothing except loosen the spacing.
    while (this._charTexts.length < chars.length) {
      const t = new PIXI.Text("", new PIXI.TextStyle(this._counterStyle));
      t.anchor.set(0.5);
      this.counterContainer.addChild(t);
      this._charTexts.push(t);
    }
    // Hide excess
    for (let i = chars.length; i < this._charTexts.length; i++) {
      this._charTexts[i].visible = false;
    }

    // Position each character in its fixed-width cell, centered around x=0
    let x = -totalWidth / 2;
    for (let i = 0; i < chars.length; i++) {
      const t = this._charTexts[i];
      t.visible = true;
      t.text = chars[i];
      t.x = x + cellWidths[i] / 2;
      t.y = 0;
      x += cellWidths[i];
    }

    // Auto-scale counter to fit — matches reference WinAnimationController pattern
    if (this.counterDetached) {
      // When detached, use SCREEN WIDTH for overflow (same as reference controller)
      // detachedScale maps local→screen, so convert maxWidth to local space
      const maxWidth = (this.dimensions?.width || 800) * 0.85;
      const localMaxWidth =
        this.detachedScaleX > 0 ? maxWidth / this.detachedScaleX : maxWidth;
      if (totalWidth > localMaxWidth && localMaxWidth > 0) {
        const scale = localMaxWidth / totalWidth;
        this.counterContainer.scale.set(
          scale * this.detachedScaleX,
          scale * this.detachedScaleY,
        );
      } else {
        this.counterContainer.scale.set(
          this.detachedScaleX,
          this.detachedScaleY,
        );
      }
    } else {
      this.counterContainer.scale.set(1);
      const maxWidth = this._counterBoneWidth * 0.9;
      if (totalWidth > maxWidth && maxWidth > 0) {
        this.counterContainer.scale.set(maxWidth / totalWidth);
      }
    }
  }

  /**
   * Show counter (visibility only, spine controls position/scale/rotation)
   */
  showCounter() {
    if (!this.counterContainer) return;
    this.counterContainer.alpha = 1;
    // Multiplier stays hidden until counter finishes (showMultiplierAndButton)
  }

  /**
   * Hide counter (visibility only, spine controls position/scale/rotation)
   */
  hideCounter() {
    if (!this.counterContainer) return;
    this.counterContainer.alpha = 0;
    if (this.counterContainer) this.counterContainer.autoUpdate = false;
    if (this.multiplierContainer) this.multiplierContainer.alpha = 0;
  }

  /**
   * Detach counter and multiplier from spine slots — they stay at fixed position,
   * unaffected by bone transforms (IN/OUT scale animations).
   * Used between tier transitions to keep counter visible.
   */
  /**
   * The bone the counter's slot hangs off — asked of the SLOT, never named.
   *
   * It was hardcoded as `value_placeholder`, and this skeleton has no such
   * bone: `txt_slot_value` hangs off `value`. So `findBone` returned null and
   * detachCounterFromSlot returned at its guard, silently, every time. The
   * counter therefore stayed a slot object across a skip and faded out and back
   * in with `in`'s own rgba keys — which is the disappearing number.
   *
   * The slot is the authority: whatever bone it is attached to is the bone that
   * drives it, in this export and the next one.
   */
  _counterBone() {
    const skeleton = this.winSpine?.skeleton;
    const bone = skeleton?.findSlot("txt_slot_value")?.bone ?? null;
    if (!bone) {
      // Loud, because the silent version of this cost an afternoon.
      console.warn(
        "[WinAnimation] No bone for txt_slot_value — the counter cannot be " +
          "detached, and a skip will fade it with the `in` clip.",
      );
    }
    return bone;
  }

  detachCounterFromSlot() {
    if (!this.counterContainer || this.counterDetached || !this.winSpine) {
      return;
    }

    // Find the bone that drives the txt_slot_value slot — see _counterBone.
    const bone = this._counterBone();
    if (!bone) {
      return;
    }

    // Ensure bone world transforms are current
    this.winSpine.skeleton.updateWorldTransform(2);

    // Calculate position: bone world coords → global → parent-local
    const globalPos = this.winSpine.toGlobal({
      x: bone.worldX,
      y: bone.worldY,
    });
    const localPos = this.container.toLocal(globalPos);

    // Capture spine pixi scale and bone scale at detach time
    // These are frozen so tier OUT/IN animations don’t corrupt the counter
    const spineScaleX = Math.abs(this.winSpine.scale.x);
    const spineScaleY = Math.abs(this.winSpine.scale.y);
    const boneScaleX = Math.sqrt(bone.a * bone.a + bone.b * bone.b);
    const boneScaleY = Math.sqrt(bone.c * bone.c + bone.d * bone.d);

    // Save for sync on resize
    this._detachSpineScaleX = spineScaleX;
    this._detachSpineScaleY = spineScaleY;
    this._detachBoneScaleX = boneScaleX;
    this._detachBoneScaleY = boneScaleY;


    // Remove from spine slots
    this.winSpine.removeSlotObject("txt_slot_value", this.counterContainer);

    // Null out slot attachments so spine doesn’t render baked-in visuals
    const valueSlot = this.winSpine.skeleton.findSlot("txt_slot_value");
    if (valueSlot) valueSlot.setAttachment(null);

    // Add to parent container (completely outside spine’s hierarchy)
    this.container.addChild(this.counterContainer);

    // Apply position and scale — matches reference WinAnimationController exactly
    this.counterContainer.x = localPos.x;
    this.counterContainer.y = localPos.y;
    this.detachedScaleX = spineScaleX * boneScaleX;
    this.detachedScaleY = spineScaleY * boneScaleY;
    this.counterContainer.scale.set(this.detachedScaleX, this.detachedScaleY);
    this.counterContainer.rotation = 0;

    this.counterDetached = true;
  }

  /**
   * Sync detached counter/multiplier position/scale with spine’s current transform.
   * Called from applyScaleAndCenter on resize to keep counter aligned.
   *
   * Uses frozen bone scale from detach time, NOT the current animated bone transforms.
   * Only the spine’s pixi scale (stable device-based value) is re-read.
   */
  syncDetachedCounter() {
    if (!this.counterDetached || !this.counterContainer || !this.winSpine)
      return;

    // Recalculate position from spine’s current pixi transform
    const bone = this._counterBone();
    if (!bone) return;

    this.winSpine.skeleton.updateWorldTransform(2);

    const globalPos = this.winSpine.toGlobal({
      x: bone.worldX,
      y: bone.worldY,
    });
    const localPos = this.container.toLocal(globalPos);

    this.counterContainer.x = localPos.x;
    this.counterContainer.y = localPos.y;

    // Scale: use FROZEN bone scale from detach time, only adjust for spine pixi scale change
    const currentSpineScaleX = Math.abs(this.winSpine.scale.x);
    const currentSpineScaleY = Math.abs(this.winSpine.scale.y);

    this.detachedScaleX = currentSpineScaleX * this._detachBoneScaleX;
    this.detachedScaleY = currentSpineScaleY * this._detachBoneScaleY;
    this.counterContainer.scale.set(this.detachedScaleX, this.detachedScaleY);


    this.updateCounterDisplay(this.currentCounterValue);
  }

  /**
   * Reattach counter and multiplier to spine slots — they follow bone transforms again.
   * Used before closing OUT animation so counter scales out with the bone.
   */
  reattachCounterToSlot() {
    if (!this.counterContainer || !this.counterDetached || !this.winSpine)
      return;

    // Remove from parent container
    if (this.counterContainer.parent) {
      this.counterContainer.parent.removeChild(this.counterContainer);
    }

    // Re-attach to spine slot
    this.winSpine.addSlotObject("txt_slot_value", this.counterContainer);

    // Reset scale to slot-local
    this.counterContainer.scale.set(1);
    this.counterContainer.x = 0;
    this.counterContainer.y = 0;
    this.counterContainer.rotation = 0;

    // Multiplier stays in its slot permanently — no reattach needed

    this.counterDetached = false;
    this.detachedScaleX = 1;
    this.detachedScaleY = 1;

    // Re-render counter at slot-local scale
    this.updateCounterDisplay(this.currentCounterValue);
  }



  /**
   * Registers the React shell's dismiss handler (fullscreen + sound + click
   * accept), so the "TAP TO CONTINUE" text can trigger the same flow as the
   * full-screen overlay when tapped directly.
   */
  setClickHandler(handler) {
    this._clickHandler = handler;
  }

  /**
   * Initialize button text and attach to spine slot with alpha 0.
   *
   * Per GDD: "Tap to Continue" only appears AFTER the win counter has finished
   * counting. Attached to slot on init (same as counter), starts invisible,
   * and the spine's OUT animation naturally carries it away — no manual hiding.
   */
  initializeButton() {
    if (!this.winSpine || !this.winSpine.skeleton) {
      return;
    }

    // Check if slot exists in spine
    const tapSlot = this.winSpine.skeleton.findSlot("txt_slot_tap_to_continue");
    if (!tapSlot) {
      return;
    }

    // Create container and attach to slot immediately (like the counter)
    this.buttonContainer = new PIXI.Container();
    this.buttonContainer.name = "win_continue_attached_to_slot";

    // Always "TAP TO CONTINUE" regardless of device
    const buttonLabel = this.t("tap_to_continue").toUpperCase();

    // Use intro_tap_to_start style — pixel properties scale proportionally with fontSize
    const style = FONT_STYLES.introTapToStart;
    const btnFontSize = 42;

    this.buttonText = new PIXI.Text(
      buttonLabel,
      new PIXI.TextStyle(scaleStyle(style, btnFontSize)),
    );
    this.buttonText.anchor.set(0.5);
    this.buttonText.alpha = 0;
    this.buttonText.visible = false;

    this.buttonContainer.addChild(this.buttonText);

    // Own hit area — hand cursor only shows over this text, not the whole
    // background overlay (matches the other popups' click-affordance pattern).
    this.buttonContainer.eventMode = "static";
    this.buttonContainer.cursor = "pointer";
    this.buttonContainer.on("pointertap", (e) => {
      if (e?.stopPropagation) e.stopPropagation();
      if (this._clickHandler) this._clickHandler(e);
    });

    // Scale text to fit within the bone's width
    const bone = tapSlot.bone;
    this._tapBoneWidth = bone ? bone.data.length || 400 : 400;
    if (this.buttonText.width > this._tapBoneWidth && this._tapBoneWidth > 0) {
      const scale = this._tapBoneWidth / this.buttonText.width;
      this.buttonContainer.scale.set(scale);
    }

    // Attach to slot — Spine auto-updates position/rotation/scale
    this.winSpine.addSlotObject("txt_slot_tap_to_continue", this.buttonContainer);

    this.applyButtonOffset();
  }

  /**
   * Lift the tap word off its bone, in SPINE UNITS.
   *
   * On the TEXT, not the container: spine rewrites the container's transform
   * every frame, so anything written there is gone by the next tick. Spine
   * units rather than a multiple of symbolHeight, because text.y is
   * spine-local and the spine's own scale carries it.
   *
   * The same method, on the same spine, with the same numbers as
   * FreeSpinTotalWin / FreeSpinMaxWin / FreeSpinZeroWin. Only Max Win shows a
   * tap word here (GDD §17.2-17.3), so this is the case where the celebration
   * and the free-spin Max Win popup must line up exactly.
   */
  applyButtonOffset() {
    if (!this.buttonText) return;
    const { buttonYOffset = 0 } = this.getDeviceMultipliers();
    this.buttonText.y = buttonYOffset;
  }

  /**
   * Show multiplier ("XX X BET") with fade-in, then conditionally show "TAP TO CONTINUE".
   * Called when counter finishes counting on the final segment.
   *
   * Per GDD §17.2-17.3:
   * - BW/MW/SMW: show multiplier only, NO CTA button. Auto-close after 3s.
   * - Max Win: show multiplier AND "Tap to Continue" button.
   */
  /**
   * ── The xBet gate ─────────────────────────────────────────────────────────
   *
   * "XX X BET" may appear under exactly two conditions, both of which must
   * hold at the same instant:
   *
   *   1. the counter has stopped, and
   *   2. no `switch` clip owns track 0.
   *
   * Nothing is ever injected into a `switch`. A skip is the
   * only way the two can disagree — it stops the counter wherever it lands,
   * which is often mid-switch — so the reveal is not a call, it is a request.
   * The request is parked and a per-frame check releases it the moment both
   * conditions are true. Asking the skeleton every frame cannot drift the way
   * a flag raised and lowered across a dozen timers did.
   */
  _requestMultiplier(kind) {
    // "withButton" wins if both are asked for — it is the superset.
    if (kind === "withButton" || !this._multiplierRequest) {
      this._multiplierRequest = kind;
    }
    if (this._multiplierGateFn) return;
    this._multiplierGateFn = () => this._pollMultiplierGate();
    PIXI.Ticker.shared.add(this._multiplierGateFn);
  }

  /** Stop watching. Called on release, on close, and on a fresh sequence. */
  _disarmMultiplierGate() {
    if (!this._multiplierGateFn) return;
    PIXI.Ticker.shared.remove(this._multiplierGateFn);
    this._multiplierGateFn = null;
  }

  _pollMultiplierGate() {
    if (!this._multiplierRequest) {
      this._disarmMultiplierGate();
      return;
    }
    if (!this.isPlaying || this.isClosing || this.isFinishing) {
      this._multiplierRequest = null;
      this._disarmMultiplierGate();
      return;
    }
    // The final tier only — an intermediate tier is not where the xBet lives.
    const onFinalTier =
      this.currentAnimationIndex === this.animationQueue.length - 1;
    // ── ASK THE SLOT, not the clip name ──
    //
    // Our text lives inside a container handed to `addSlotObject`, and the
    // runtime rewrites that container's alpha every frame from the slot's own
    // colour. So the spine decides whether the xBet is on screen; we only decide
    // what the text inside it says and how opaque the TEXT is. Both have to
    // agree, and only one of them is ours.
    //
    // `win_celebration` keys that slot like this:
    //
    //   in       ffffff00 -> ffffff00 -> ffffffff   invisible until the last key
    //   switch   no key                             setup, visible
    //   idle     no key                             setup, visible
    //   out      ffffffff -> ffffff00
    //
    // Which is why "anything but `switch`" was not enough. A skip released the
    // reveal during the final tier's `in`, where the slot is still at zero: our
    // fade ran invisibly, and then `in`'s last key OPENED the slot and the value
    // popped in on its own. Appear at the skip, vanish under the new `in`,
    // appear again as it ends — the twice.
    //
    // Waiting for the idle by name would fix this one skeleton and break on the
    // next re-export. The live slot alpha is the condition itself: when the
    // runtime is drawing that container at full strength, the value may appear,
    // and never before.
    const slotName = this._multiplierSlotName;
    const slotAlpha = slotName
      ? (this.winSpine?.skeleton?.findSlot(slotName)?.color?.a ?? 1)
      : 1;
    if (!onFinalTier || slotAlpha < 0.99 || this.isCounterAnimating()) return;

    const kind = this._multiplierRequest;
    this._multiplierRequest = null;
    this._disarmMultiplierGate();
    this._revealMultiplier(kind);
  }

  _revealMultiplier(kind) {
    if (kind === "withButton") this._revealMultiplierAndButton();
    else this._revealMultiplierOnly();
  }

  showMultiplierAndButton() {
    this._requestMultiplier("withButton");
  }

  /**
   * Fade "XX X BET" in — ONCE per celebration, whoever asks.
   *
   * ── Why a latch, and not a tidier call graph ──
   *
   * Four places ask for this, and on a SKIP they overlap: the final counter
   * segment's own completion (§17.2's "single source of truth"),
   * skipToFinalAnimation, its no-IN branch, and handleSkipDuringFinalAnimation.
   * A skip jumps the counter AND reaches the final animation, so two of them
   * fire for one win — and each set `alpha = 0` and started a fresh fade, which
   * slams a line that is already on screen back to nothing and brings it in
   * again. That is the flash, and the `switch` gate cannot stop it: by then the
   * switch is long gone and both callers pass the gate honestly.
   *
   * All four are right about WHEN the value may appear. None of them can know
   * whether it already has. So that decision lives here, in the one place that
   * draws it.
   *
   * `anime.remove` as well as the latch: two tweens on one alpha read as a
   * stutter, and a fade interrupted by an identical fade is the same bug in a
   * different hat.
   */
  _fadeInMultiplier(duration) {
    if (this._multiplierRevealed) return;
    if (!this.multiplierText || !(this.betAmount > 0)) return;

    this._multiplierRevealed = true;
    this.updateMultiplierDisplay(this.totalWinAmount);
    anime.remove(this.multiplierText);
    this.multiplierText.visible = true;
    this.multiplierText.alpha = 0;
    anime({
      targets: this.multiplierText,
      alpha: 1,
      duration,
      easing: "easeOutCubic",
    });
  }

  _revealMultiplierAndButton() {
    // 200ms after counter finishes → fade in X BET
    const multTimeout = new TickerTimeout(() => {
      if (!this.isPlaying) return;

      this._fadeInMultiplier(400);

      // GDD §17.2: CTA button ONLY on Max Win — BW/MW/SMW do NOT have a CTA button
      if (this.finalWinType === "max_win") {
        // 200ms after X BET appears → fade in TAP TO CONTINUE
        const btnTimeout = new TickerTimeout(() => {
          if (this.isPlaying && this.buttonText) {
            this.buttonText.visible = true;
            anime({
              targets: this.buttonText,
              alpha: 1,
              duration: 400,
              easing: 'easeOutCubic',
            });
          }
        }, 200);
        this.currentTimeouts.push(btnTimeout);
      }
    }, 200);
    this.currentTimeouts.push(multTimeout);
  }

  /**
   * Show only the multiplier ("XX X BET") without the CTA button.
   * Used for BW/MW/SMW per GDD §17.2-17.3.
   */
  showMultiplier() {
    if (!this.isPlaying) return;
    this._requestMultiplier("multiplier");
  }

  _revealMultiplierOnly() {
    this._fadeInMultiplier(200);
  }

  /**
   * Legacy alias — routes to showMultiplierAndButton.
   */
  /**
   * No-op in Mad Labs.
   *
   * The skeleton still carries a `txt_slot_tap_to_continue` slot, but NO skin
   * gives it an attachment — there is no such prompt in this game's design.
   * The celebration ends on the auto-close timer, or on a tap anywhere on the
   * overlay, both of which are unchanged.
   *
   * Kept as a no-op rather than deleted so the four call sites in the win
   * sequence stay readable as "this is where the prompt used to appear".
   */
  showTapToContinue() {
    // Intentionally empty — see above.
  }

  /**
   * Start counter segment for the current tier.
   * Each tier counts a proportional portion of the total win during its IDLE phase.
   * This ensures the counter only advances while visible — no off-screen counting.
   *
   * @param {boolean} isFinalSegment - If true, triggers completion callback when done
   */
  startTierCounter(isFinalSegment) {
    if (!this.counterSegmentTargets || this.currentSegmentIndex >= this.counterSegmentTargets.length) {
      return;
    }

    const segmentTarget = this.counterSegmentTargets[this.currentSegmentIndex];
    const segmentFrom = this.currentCounterValue || 0;
    // Counter runs for the full tier. Only the FIRST tier is entered through
    // IN — every later one is entered through the shorter SWITCH, so the
    // segment has to match or the counter drifts against the visuals.
    const segmentDuration =
      this.currentSegmentIndex === 0
        ? this.getTierDuration()
        : this.getSwitchTierDuration();

    this.currentSegmentIndex++;

    const onComplete = isFinalSegment
      ? () => {
          if (!this.isMaxWinFinal) {
            if (this.dispatch) {
              this.dispatch(setWinAnimationCounterFinished(true));
            }
            // GDD §17.2: Show multiplier when counter finishes (single source of truth)
            this.showMultiplier();
            // GDD §17.3: Auto-close after 3s for BW/MW/SMW
            this.startAutoCloseTimer();
          }
        }
      : null;

    // If counter has already reached the target (e.g. win at exact threshold),
    // fire completion immediately — no point animating from 1000→1000
    if (segmentFrom >= segmentTarget) {
      if (onComplete) onComplete();
      return;
    }

    if (this.totalWinAmount > 0) {
      const easing = isFinalSegment ? "easeOutQuad" : "linear";

      this.startCounterAnimation(segmentFrom, segmentTarget, segmentDuration, onComplete, easing);
    }
  }

  /**
   * Animate counter value from one number to another
   *
   * @param {number} fromValue - Starting value
   * @param {number} toValue - Target value
   * @param {number} duration - Animation duration in ms
   * @param {Function} onComplete - Callback when animation completes
   * @param {string} easing - Anime.js easing function (default: easeOutQuad)
   */
  startCounterAnimation(fromValue, toValue, duration, onComplete, easing = "easeOutQuad") {
    if (this.counterAnimationRef) {
      this.counterAnimationRef.pause();
      this.counterAnimationRef = null;
    }

    this.currentCounterValue = fromValue;
    this.updateCounterDisplay(fromValue);

    this.startCounterSound();

    this.counterAnimationRef = anime({
      targets: this,
      currentCounterValue: toValue,
      duration: duration,
      easing: easing,
      update: () => {
        this.updateCounterDisplay(this.currentCounterValue);
      },
      complete: () => {
        this.currentCounterValue = toValue;
        this.updateCounterDisplay(toValue);
        this.counterAnimationRef = null;

        // The counter runs as ONE sound across the whole tier chain. Only the
        // segment that reaches the total ends it.
        if (toValue >= (this.totalWinAmount ?? toValue)) {
          this.stopCounterSound(true);
        }

        // Call completion callback
        if (onComplete && typeof onComplete === "function") {
          onComplete();
        }
      },
    });
  }

  /**
   * Start the counter loop, if it is not already running.
   *
   * `counter` is a LOOPING Howl, and Howler answers play() on a running Howl
   * with a SECOND sound id rather than a restart — the two then stack. The
   * counter is also one continuous sound across the whole tier chain, not one
   * per tier, so this is called by every segment but only ever starts it once.
   */
  startCounterSound() {
    if (this.counterSoundPlaying) return;
    this.counterSoundPlaying = true;
    soundManager.play(SOUND_NAME.counter);
  }

  /**
   * Stop the counter loop.
   *
   * `playEnd` marks the moment the numbers land, using the SAME `counter`
   * sound played ONCE instead of looping — there is no separate end asset.
   *
   * Deliberately NOT guarded on `counterSoundPlaying`: this Howl is shared
   * with the other win screen, so whichever controller stops it must always
   * reach soundManager. Guarding the stop is what left it looping for ever.
   */
  stopCounterSound(playEnd = false) {
    this.counterSoundPlaying = false;
    soundManager.stop(SOUND_NAME.counter);
    if (playEnd) soundManager.playOnce(SOUND_NAME.counter);
  }

  /**
   * Instantly set counter to specific value (skips animation)
   *
   * @param {number} value - Value to display
   */
  jumpCounterToValue(value) {
    if (this.counterAnimationRef) {
      this.counterAnimationRef.pause();
      this.counterAnimationRef = null;
    }

    this.currentCounterValue = value;
    this.updateCounterDisplay(value);
    this.stopCounterSound(true);
  }

  /**
   * Reset counter to zero
   */
  resetCounter() {
    if (this.counterAnimationRef) {
      this.counterAnimationRef.pause();
      this.counterAnimationRef = null;
    }

    this.currentCounterValue = 0;
    this.updateCounterDisplay(0);
    this.stopCounterSound(false);
  }

  /**
   * Check if counter value is currently animating
   *
   * @returns {boolean} True if counter is animating
   */
  isCounterAnimating() {
    return this.counterAnimationRef !== null;
  }

  /**
   * Get current counter value
   *
   * @returns {number} Current counter value
   */
  getCurrentCounterValue() {
    return this.currentCounterValue || 0;
  }

  /**
   * Calculate which win animations should play based on win multiplier
   *
   * Determines progressive win sequence based on win amount relative to bet:
   * - 20x+ bet = BIG_WIN animation
   * - 40x+ bet = BIG_WIN + MEGA_WIN animations
   * - 60x+ bet = BIG_WIN + MEGA_WIN + SUPER_MEGA_WIN animations
   *
   * @param {number} winAmount - Total win amount in credits
   * @param {number} betAmount - Bet amount for multiplier calculation
   * @returns {string[]} Array of win types in sequence (e.g., ['big_win', 'mega_win'])
   */
  calculateAnimationSequence(winAmount, betAmount) {
    if (!winAmount || !betAmount || winAmount <= 0 || betAmount <= 0) {
      return [];
    }

    // Read pre-computed winType from Redux state (set by RGS adapter).
    //
    // ── Which round is playing ──
    //
    // game.currentRound is the BASE board's. During free spins it is frozen on
    // the trigger spin, which paid 0 and therefore carries winType "" — so this
    // returned an empty sequence and the celebration never played.
    //
    // That was not merely a missing animation, it DEADLOCKED the round: the win
    // controller had already dispatched setWinAnimationsPlaying(true) from the
    // bonus round's own isAnimatedWin, play() bailed at the empty-queue guard
    // below, and the only setWinAnimationsPlaying(false) in this file sits in
    // the finish path that guard skips. FreeSpinBasicControlContext gates the
    // next spin on that flag, so the round stopped with spins left and the
    // session had to be abandoned.
    //
    // Same switch WinDisplay makes: read the round that is actually on screen.
    const state = store.getState();
    const freeSpins = state.freeSpins;
    const activeRound = freeSpins?.active
      ? freeSpins.currentRound
      : state.game.currentRound;
    const finalWinType = activeRound?.winType || "";

    // If it's not an animated win type, return empty array
    if (!finalWinType || !ANIMATED_WIN_TYPES.includes(finalWinType)) {
      return [];
    }

    // ── MAX WIN PLAYS ALONE ──
    //
    // Mad Labs GDD §11: Max Win "appears ~2s into the win count, skipping the
    // BW->MW->SMW sequence entirely". Zeus GDD §18.2 step 6 says it again:
    // "(WITHOUT going through BW->MW->SMW)". Zeus §17.1's "higher sequences
    // include previous ones" is for BIG / MEGA / SUPER MEGA only — Max Win is
    // carved out of it in both documents. Until 2026-09-22 this chained.
    //
    // FREE SPINS never reaches here on a capped round at all: the whole
    // celebration is suppressed at the flag, beside the replay suppression
    // that already existed — see isAnimatedWin in useFreeSpinBasicWinController.
    // The round's own MAX WIN popup is the single fanfare there. This branch is
    // the BASE game's, where nothing closes a round and the celebration is the
    // only Max Win.
    //
    // MAX_WIN.ENABLED false demotes rather than blanks, so turning the feature
    // off still celebrates a capped win instead of showing nothing.
    const effectiveWinType =
      finalWinType === "max_win" && !FEATURES_CONFIG.MAX_WIN.ENABLED
        ? "supermega_win"
        : finalWinType;

    if (effectiveWinType === "max_win") return ["max_win"];

    // Build sequence dynamically from provider's animated win types
    // Order win types by their thresholds (ascending)
    const winTypeOrder = ANIMATED_WIN_TYPES.map((winType) => ({
      type: winType,
      threshold: WIN_TYPE_THRESHOLDS[winType] || 0,
    }))
      .sort((a, b) => a.threshold - b.threshold)
      .map((item) => item.type);

    const sequence = [];
    const multiplier = winAmount / betAmount;

    // Add all win types that the multiplier qualifies for.
    //
    // Only BIG / MEGA / SUPER MEGA ever reach here — max_win returned above —
    // so the loop cannot run past supermega and needs no max_win case.
    for (const winType of winTypeOrder) {
      if (this.shouldPlayWinType(winType, multiplier)) {
        sequence.push(winType);
      }

      // Stop when we reach the final win type
      if (winType === effectiveWinType) {
        break;
      }
    }

    return sequence;
  }

  /**
   * Check if a win type should be played based on multiplier
   */
  shouldPlayWinType(winType, multiplier) {
    const threshold = WIN_TYPE_THRESHOLDS[winType] || 0;
    return threshold > 0 && multiplier >= threshold;
  }

  /**
   * Play the sound for the current win animation phase.
   *
   * Two shapes are supported:
   *  - a phase set, {winType}_{phase} — still used by max_win and total_win;
   *  - ONE sound for the whole level, named exactly {winType} — big_win,
   *    mega_win, supermega_win.
   *
   * `win_music` plays under the WHOLE sequence. A level sound plays ON TOP of
   * it, once, the first time that level is reached — whichever phase opens it.
   * The first level opens on "in"; every later one opens on the "idle" that
   * follows its SWITCH, so keying on the phase name would leave mega and
   * supermega silent. Keying on the level instead also stops a level from
   * sounding twice when its "idle" is re-entered.
   */
  playWinPhaseSound = (winType, phase) => {
    if (!this.dispatch) return;

    const phaseSoundKey = `${winType}_${phase}`;
    if (SOUND_NAME[phaseSoundKey]) {
      try {
        soundManager.play(SOUND_NAME[phaseSoundKey]);
      } catch (error) {
        // Silent fail
      }
      return;
    }

    if (!SOUND_NAME[winType]) return;
    if (!this.playedLevelSounds) this.playedLevelSounds = new Set();
    if (this.playedLevelSounds.has(winType)) return;
    this.playedLevelSounds.add(winType);

    try {
      // ONE banner sounds at a time, over win_music.
      //
      // Every clip is LONGER than the tier it belongs to — big_win 3.53s over
      // a 3.00s tier (in 1.0 + idle 2.0), mega_win 3.53s over a 2.67s tier
      // (switch 0.667 + idle 2.0), supermega_win 3.03s over 2.67s. Left alone
      // the next banner starts while the previous is still sounding, so two
      // overlap and something plays for 100% of the 8.3s sequence. win_music
      // then only surfaces in the moment one of them ends.
      if (this.currentLevelSound && this.currentLevelSound !== winType) {
        soundManager.fadeOutOneShot(
          SOUND_NAME[this.currentLevelSound],
          BANNER_CUT_FADE_MS,
        );
      }
      this.currentLevelSound = winType;
      soundManager.play(SOUND_NAME[winType]);
    } catch (error) {
      // Silent fail
    }
  };

  /**
   * Fade in win music on loop for entire sequence (replaces individual win sounds)
   */
  playMainWinSound = () => {
    try {
      // Wait only as long as the background takes to go, then take over.
      const fadeDelay = new TickerTimeout(() => {
        const winMusic = HowlCache[SOUND_NAME.win_music];
        if (winMusic) {
          winMusic.loop(true); // Ensure it loops
          soundManager.fadeIn(SOUND_NAME.win_music, WIN_MUSIC_CROSSFADE_MS);
        }
      }, WIN_MUSIC_CROSSFADE_MS);
      this.currentTimeouts.push(fadeDelay);
    } catch (error) {
      // Silent fail
    }
  };

  /**
   * Fade out background music if it's playing (smooth transition to win music)
   */
  pauseBackgroundMusic() {
    try {
      // Fade out whichever background track is up — it does not matter which
      // room we are in. fadeOut() takes the paired ambient track down with it.
      const candidates = [
        SOUND_NAME.background_music,
        SOUND_NAME.feature_background_music,
        SOUND_NAME.free_spin_background_music,
      ];
      const playingName = candidates.find(
        (name) => name && HowlCache[name]?.playing(),
      );

      if (playingName) {
        this.backgroundMusicWasPlaying = true;
        this.backgroundMusicType = playingName;
        soundManager.fadeOut(playingName, WIN_MUSIC_CROSSFADE_MS);
      } else {
        this.backgroundMusicWasPlaying = false;
        this.backgroundMusicType = null;
      }
    } catch (error) {
      // Silent fail
      this.backgroundMusicWasPlaying = false;
      this.backgroundMusicType = null;
    }
  }

  /**
   * Fade in background music if it was playing before (smooth transition from win music)
   */
  resumeBackgroundMusic(duration = WIN_MUSIC_OUTRO_FADE_MIN_MS) {
    try {
      if (this.backgroundMusicWasPlaying && this.backgroundMusicType) {
        soundManager.fadeIn(this.backgroundMusicType, duration);
      }
      this.backgroundMusicWasPlaying = false; // Reset flag
      this.backgroundMusicType = null;
    } catch (error) {
      // Silent fail
      this.backgroundMusicWasPlaying = false;
      this.backgroundMusicType = null;
    }
  }

  /**
   * Stop banner sounds (win sounds specific to each animation)
   * Uses the win sound directly (no banner versions exist)
   */
  stopBannerSounds() {
    if (!this.dispatch) return;

    try {
      // Stop win sounds used for animations (no banner versions exist)
      const bannerSounds = [
        SOUND_NAME.big_win,
        SOUND_NAME.mega_win,
        SOUND_NAME.supermega_win,
        SOUND_NAME.max_win_in,
      ];

      bannerSounds.forEach((soundName) => {
        if (soundName) {
          soundManager.fadeOutOneShot(soundName, BANNER_CUT_FADE_MS);
        }
      });
      this.currentLevelSound = null;
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Stop all win sounds (banner + main)
   */
  stopAllWinSounds() {
    this.stopBannerSounds();
    this.stopWinSounds();
    this.stopCounterSound(false);
  }

  /**
   * Fade out main win music (replaces stopping individual win sounds)
   */
  stopWinSounds() {
    if (!this.dispatch) return;

    try {
      // Fade out win_music instead of stopping it abruptly
      if (SOUND_NAME.win_music) {
        soundManager.fadeOut(SOUND_NAME.win_music, MUSIC_FADE_MS);
      }
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Start the progressive win animation sequence
   *
   * Determines which win animations to play based on win amount and bet size,
   * then plays them in sequence (BIG → SUPER → MEGA → EPIC).
   * Each animation follows: IN → IDLE → OUT pattern.
   * Counter animation runs in sync with spine animations.
   *
   * @param {number} winAmount - Total win amount in credits
   * @param {number} betAmount - Bet amount for win type threshold calculation
   * @param {string[]|null} [forcedSequence] - Play THESE tiers instead of the
   *   ones the win amount earns, e.g. ["big_win","mega_win","supermega_win"].
   *   For the dev panel only: it has no round, so calculateAnimationSequence
   *   would read an empty winType off Redux and return nothing. Left out, the
   *   real game path is untouched.
   * @returns {Promise} Resolves when animation sequence is complete
   */
  startSequence(winAmount, betAmount, forcedSequence = null) {
    return new Promise((resolve, reject) => {
      // Prevent retriggering during finishing phase and duplicate wins.
      // The duplicate guard is skipped for a forced sequence, or the dev
      // button would fire once and then look broken on every later press.
      if (
        this.isPlaying ||
        this.isFinishing ||
        (!forcedSequence && winAmount === this.lastWinAmount) ||
        !this.winSpine ||
        !this.winSpine.state
      ) {
        resolve();
        return;
      }

      // Calculate which animations to play
      this.animationQueue = forcedSequence
        ? [...forcedSequence]
        : this.calculateAnimationSequence(winAmount, betAmount);

      if (this.animationQueue.length === 0) {
        resolve();
        return;
      }

      this.isPlaying = true;
      this.currentAnimationIndex = 0; // Start at 0 for first animation
      this.finalWinType = this.animationQueue[this.animationQueue.length - 1];
      this.totalWinAmount = winAmount;
      this.betAmount = betAmount;
      this.lastWinAmount = winAmount; // Track this win amount to prevent restart
      this.counterJumpedToFinal = false; // Reset counter jump flag
      // The xBet may appear once per celebration — see _fadeInMultiplier.
      this._multiplierRevealed = false;

      // Reset user interaction guards at the beginning of a sequence
      this.hasHandledSkipClick = false;
      this.hasHandledCloseClick = false;
      this.isHandlingUserClick = false;
      this.isWaitingForClick = false; // Reset waiting state for new sequence
      this.isClosing = false; // Reset closing state for new sequence

      // SKIP PROTECTION: Block skipping for the first second
      this.canSkipAnimation = false;
      if (this.skipProtectionTimeout) {
        this.skipProtectionTimeout.clear();
      }
      this.skipProtectionTimeout = new TickerTimeout(() => {
        // Safety check: only enable skip if animation is still playing and not finished
        if (
          this.isPlaying &&
          !(this.hasHandledSkipClick && this.hasHandledCloseClick)
        ) {
          this.canSkipAnimation = true;
        }
      }, 300); // 300ms protection — prevents spin-button bleed-through

      // Enable scaling calculations when animation becomes active
      this.shouldBeActive = true;

      // Each level sounds once per sequence — clear the record for this one.
      this.playedLevelSounds = new Set();
      this.currentLevelSound = null;
      this.stopCounterSound(false);

      // Pause background music before starting win animation
      this.pauseBackgroundMusic();

      // Spine is already positioned correctly at all times (via updateDimensions)
      // Just ensure final positioning check before showing (spine is always warmed up and positioned)
      if (
        this.winSpine &&
        this.symbolHeight &&
        this.symbolHeight > 0 &&
        this.dimensions
      ) {
        this.applyScaleAndCenter();
      }

      // Show and start animation - positioning is already correct since spine is always maintained
      this.showAndStartAnimation(resolve, reject);
    });
  }

  /**
   * Show spine, counter, overlay and start animation (extracted for reuse)
   */
  showAndStartAnimation(resolve, reject) {
    // Ensure "Tap to Continue" and multiplier start hidden for this new sequence
    if (this.buttonText) {
      this.buttonText.visible = false;
      this.buttonText.alpha = 0;
    }
    if (this.multiplierText) {
      anime.remove(this.multiplierText);
      this._multiplierRevealed = false;
      this.multiplierText.visible = false;
      this.multiplierText.alpha = 0;
    }
    // A fresh sequence inherits no parked request from the last one.
    this._multiplierRequest = null;
    this._disarmMultiplierGate();
    // Fade in spine smoothly (synchronized with counter/overlay)
    if (this.winSpine) {
      // ── Woken HERE, because this path shows it with a TWEEN ──
      //
      // finishAnimation and stopWinAnimation now put this spine to sleep, and
      // the rule for that is: wake it wherever it is shown again. Everywhere
      // else that happens at a `setAnimation`, and all sixteen of those raise
      // `autoUpdate` already. This one does not — it raises alpha directly, and
      // `startFirstAnimation` below is a promise chain, so the skeleton could
      // be on screen and frozen for the whole 350ms fade before anything set a
      // track. Raised here instead, one line before the alpha it belongs to.
      this.winSpine.autoUpdate = true;
      anime({
        targets: this.winSpine,
        alpha: [0.001, 1],
        duration: 350, // Match counter fade-in duration
        easing: "easeOutQuad",
      });
    }

    this.isVisible = true;
    this.currentAnimationPhase = "spine-animation";
    this.phaseStartTime = performance.now();
    this.phaseProgress = 0;

    // Initialize counter
    this.resetCounter();
    this.showCounter();


    // Show background overlay (synchronized)
    this.overlayRef.current?.fadeInBackground();

    // Start animation after positioning is confirmed
    this.startFirstAnimation().then(resolve).catch(reject);
  }



  /**
   * Play the next animation in the queue.
   * Detaches counter from slot (bone forced to rest scale 1.0) so it stays
   * frozen while the new tier's IN animation plays behind it.
   */
  playNextAnimation() {
    return new Promise((resolve, reject) => {
      if (this.currentAnimationIndex >= this.animationQueue.length) {
        resolve();
        return;
      }

      const winType = this.animationQueue[this.currentAnimationIndex];
      const isLastAnimation =
        this.currentAnimationIndex === this.animationQueue.length - 1;


      // Play banner sound for this animation
      this.playWinPhaseSound(winType, "in");

      // Snap counter to previous segment's target — eliminates rAF/setTimeout drift
      const prevSegmentTarget = this.counterSegmentTargets[this.currentSegmentIndex - 1];
      if (prevSegmentTarget != null && this.counterAnimationRef) {
        this.counterAnimationRef.pause();
        this.counterAnimationRef = null;
        this.currentCounterValue = prevSegmentTarget;
        this.updateCounterDisplay(prevSegmentTarget);
      }

      // ── The counter is NOT detached here, and that is correct ──
      //
      // Measured off win_celebration rather than assumed:
      //
      //   switch   keys neither the bone nor the slot's colour
      //   idle     keys the bone's SCALE only — the pulse we want the counter
      //            to breathe with, which is the reason to stay attached
      //   in/out   key the slot's rgba, 00 -> ff and ff -> 00
      //
      // So a tier transition has nothing to shield the counter from. The one
      // case that does is a SKIP, which replays `in` — see
      // skipToFinalAnimation, the only detach call in this file.

      // Don't clearTracks() — that flashes the setup pose. setAnimation below
      // replaces the track.
      this.winSpine.state.clearListeners();

      // NOTE: no setSkin() here. The swap happens at the `switch` event, half
      // way through the transition, so the outgoing tier is still on screen
      // for the first half of it.

      const { idleAnimation, switchAnimation } = this.findAnimations();

      if (isLastAnimation) {
        this.playFinalAnimation(switchAnimation, idleAnimation, resolve, reject);
      } else {
        this.playIntermediateAnimation(
          switchAnimation,
          idleAnimation,
          resolve,
          reject,
        );
      }
    });
  }

  /**
   * Start first animation in the sequence
   */
  startFirstAnimation() {
    return new Promise((resolve, reject) => {
      if (
        !this.winSpine ||
        !this.winSpine.state ||
        this.animationQueue.length === 0
      ) {
        resolve();
        return;
      }

      const winType = this.animationQueue[0]; // Start with first animation
      const isLastAnimation = this.animationQueue.length === 1;

      // Play main win sound once at animation start (based on final win type)
      this.playMainWinSound();

      // Play banner sound for first animation
      this.playWinPhaseSound(winType, "in");

      // Setup spine animation
      this.winSpine.state.clearTracks();
      this.winSpine.state.clearListeners();

      // Set skin for the first tier
      this.setSkin(winType);

      // Get animation names (shared: in, idle, out)
      const { inAnimation, idleAnimation } = this.findAnimations();

      // Calculate counter segments — one per visible tier
      // Counter only runs during IDLE phases (visible), not during IN transitions
      const isMaxWinFinal = this.finalWinType === "max_win" && !isLastAnimation;
      const counterTarget = isMaxWinFinal
        ? this.totalWinAmount - 1
        : this.totalWinAmount;
      const counterTiers = isMaxWinFinal
        ? this.animationQueue.length - 1
        : this.animationQueue.length;

      // Pre-compute per-tier counter targets using actual GDD thresholds
      // Each tier transition shows the exact threshold value (e.g., 40×bet for mega_win)
      this.counterSegmentTargets = [];
      for (let i = 0; i < counterTiers; i++) {
        if (i < counterTiers - 1) {
          // Intermediate segments: target = NEXT tier's threshold × bet
          // This means the counter hits exactly the threshold when the next tier appears
          const nextTierType = this.animationQueue[i + 1];
          const nextThreshold = WIN_TYPE_THRESHOLDS[nextTierType] || 0;
          const thresholdValue = Math.round(nextThreshold * this.betAmount);
          // Clamp to counterTarget in case threshold overshoots actual win
          this.counterSegmentTargets.push(Math.min(thresholdValue, counterTarget));
        } else {
          // Final segment: always target the full win amount
          this.counterSegmentTargets.push(counterTarget);
        }
      }
      this.currentSegmentIndex = 0;
      this.isMaxWinFinal = isMaxWinFinal;


      // First animation always plays IN → IDLE (spine scales counter in via bone)
      if (inAnimation && idleAnimation) {
        // Play in animation for its actual duration
        if (this.winSpine) this.winSpine.autoUpdate = true;
        this.winSpine.state.setAnimation(0, inAnimation, false);

        // Start counter immediately — counts during IN + IDLE
        this.startTierCounter(isLastAnimation);

        // Get actual duration for in animation from spine data
        const inDuration = this.getSpineAnimationDuration("in");

        // After IN completes, start looping idle animation
        const inTimeout = new TickerTimeout(() => {
          if (this.isPlaying) {
            // Counter stays in slot — let it pulse with IDLE bone (1.0↔1.166)
            if (this.winSpine) this.winSpine.autoUpdate = true;
            this.winSpine.state.addAnimation(0, idleAnimation, true, 0);
            this.playWinPhaseSound(this.animationQueue[this.currentAnimationIndex] || this.finalWinType, "idle");

            if (isLastAnimation) {
              // Single animation or last in sequence: wait for user click
              this.isWaitingForClick = true;
              this.currentAnimationPhase = "waiting-for-click";
              this.phaseStartTime = performance.now();
              this.phaseProgress = 0;
              resolve();
            }
          }
        }, inDuration);
        this.currentTimeouts.push(inTimeout);

        if (!isLastAnimation) {
          // Single timer for full tier duration — avoids nested TickerTimeout drift
          const tierTimeout = new TickerTimeout(() => {
            if (this.isPlaying) {
              this.currentAnimationIndex++;
              this.phaseStartTime = performance.now();
              this.phaseProgress = 0;
              this.playNextAnimation().then(resolve).catch(reject);
            }
          }, this.getTierDuration());
          this.currentTimeouts.push(tierTimeout);
        }
      } else if (idleAnimation) {
        // Fallback: just loop idle animation
        if (this.winSpine) this.winSpine.autoUpdate = true;
        this.winSpine.state.setAnimation(0, idleAnimation, true);
        this.startTierCounter(isLastAnimation);
        if (isLastAnimation) {
          this.isWaitingForClick = true;
          this.currentAnimationPhase = "waiting-for-click";
          this.phaseStartTime = performance.now();
          this.phaseProgress = 0;
        }
        resolve();
      } else {
        resolve();
      }
    });
  }

  /**
   * Get animation duration from spine data with detailed extraction
   */
  getSpineAnimationDuration(animationName) {
    if (
      !this.winSpine ||
      !this.winSpine.skeleton ||
      !this.winSpine.skeleton.data ||
      !this.winSpine.skeleton.data.animations
    ) {
      return 1000; // Default fallback
    }

    try {
      // Method 1: Try to get from skeleton data animations array
      const animation = this.winSpine.skeleton.data.animations.find(
        (anim) => anim.name === animationName,
      );

      if (animation && animation.duration) {
        const durationMs = Math.round(animation.duration * 1000);
        return durationMs;
      }

      // Method 2: Try to get from spine state data
      if (this.winSpine.state && this.winSpine.state.data) {
        const stateAnimation =
          this.winSpine.state.data.skeletonData.animations.find(
            (anim) => anim.name === animationName,
          );

        if (stateAnimation && stateAnimation.duration) {
          const durationMs = Math.round(stateAnimation.duration * 1000);
          return durationMs;
        }
      }

      // Method 3: Try to access the raw JSON data if available
      if (this.winSpine.skeleton.data.name && PIXI.Assets.cache) {
        // This might contain the original JSON data
        const skeletonAsset = Object.values(PIXI.Assets.cache._cache).find(
          (asset) =>
            asset && asset.animations && asset.animations[animationName],
        );

        if (skeletonAsset && skeletonAsset.animations[animationName]) {
          // Calculate duration from keyframes if available
          const animData = skeletonAsset.animations[animationName];
          let maxTime = 0;

          // Check all tracks for the longest duration
          Object.values(animData).forEach((track) => {
            if (Array.isArray(track)) {
              track.forEach((keyframe) => {
                if (keyframe.time && keyframe.time > maxTime) {
                  maxTime = keyframe.time;
                }
              });
            }
          });

          if (maxTime > 0) {
            const durationMs = Math.round(maxTime * 1000);
            return durationMs;
          }
        }
      }

      return 1000; // Default fallback
    } catch (error) {
      return 1000; // Default fallback
    }
  }

  /**
   * Get the total duration of one tier (IN + IDLE) from the spine data.
   * Extracted dynamically — works with any spine asset, no hardcoded values.
   *
   * @returns {number} Total tier duration in ms
   */
  getTierDuration() {
    const inDuration = this.getSpineAnimationDuration("in");
    const idleDuration = this.getSpineAnimationDuration("idle");
    return inDuration + idleDuration;
  }

  /**
   * Play intermediate animation — IN → IDLE, counter stays in slot.
   * Counter segment starts when IDLE begins (visible), not during IN (invisible).
   */
  playIntermediateAnimation(switchAnimation, idleAnimation, resolve, reject) {
    const winType = this.animationQueue[this.currentAnimationIndex];
    const tierDuration = this.getSwitchTierDuration();

    // Counter runs across the whole tier — SWITCH and IDLE.
    this.startTierCounter(false);

    if (switchAnimation && idleAnimation) {
      // SWITCH, with the skin swapped at its midpoint event.
      const switchDuration = this._playSwitchInto(switchAnimation, winType);

      // Once the transition finishes, settle into the new tier's idle.
      const switchTimeout = new TickerTimeout(() => {
        if (this.isPlaying) {
          if (this.winSpine) this.winSpine.autoUpdate = true;
          this.winSpine.state.setAnimation(0, idleAnimation, true);
          // The idle owns the screen now — release anything held back.
        }
      }, switchDuration);
      this.currentTimeouts.push(switchTimeout);

      // Single timer for full tier duration — avoids nested TickerTimeout drift
      const tierTimeout = new TickerTimeout(() => {
        if (this.isPlaying) {
          this.currentAnimationIndex++;
          this.phaseStartTime = performance.now();
          this.phaseProgress = 0;
          this.playNextAnimation().then(resolve).catch(reject);
        }
      }, tierDuration);
      this.currentTimeouts.push(tierTimeout);
    } else if (idleAnimation) {
      // Fallback: the skeleton has no switch animation. Hard-swap the skin and
      // idle — visibly abrupt, but never leaves the wrong tier on screen.
      this.setSkin(winType);
      if (this.winSpine) this.winSpine.autoUpdate = true;
      this.winSpine.state.setAnimation(0, idleAnimation, true);

      const timeout = new TickerTimeout(() => {
        if (this.isPlaying) {
          this.currentAnimationIndex++;
          this.phaseStartTime = performance.now();
          this.phaseProgress = 0;
          this.playNextAnimation().then(resolve).catch(reject);
        }
      }, tierDuration);
      this.currentTimeouts.push(timeout);
    } else {
      this.currentAnimationIndex++;
      this.playNextAnimation().then(resolve).catch(reject);
    }
  }

  /**
   * Play final animation — IN → IDLE, wait for user click, OUT plays on close.
   * Counter segment starts when IDLE begins (visible).
   */
  playFinalAnimation(switchAnimation, idleAnimation, resolve, reject) {
    const winType = this.animationQueue[this.currentAnimationIndex];

    // ── MAX WIN keeps the WIN MUSIC. There is no separate max-win track. ──
    //
    // This used to stop win_music and fade in `max_win_loop`, which has no file
    // in this game — so the biggest moment in it played its one-shot and then
    // sat in SILENCE until the player clicked, for up to 30 seconds.
    //
    // win_music already loops for the whole celebration and already crossfades
    // back to the background on the `out` clip, so leaving it alone is both the
    // fix and the feature: the max win rides the music that is already playing.
    // Vladimir, 2026-09-23 — *"we cant end on big win right, so we are free to
    // play win music on loop in max win popup."*

    // MAX WIN: jump counter from totalWinAmount-1 to totalWinAmount
    if (winType === "max_win") {
      if (!this.counterJumpedToFinal) {
        this.jumpCounterToValue(this.totalWinAmount);
        this.counterJumpedToFinal = true;
        if (this.dispatch) {
          this.dispatch(setWinAnimationCounterFinished(true));
        }
        this.startAutoCloseTimer();
      }
    }

    if (switchAnimation && idleAnimation) {
      // SWITCH into the final tier, skin swapped at its midpoint event.
      const switchDuration = this._playSwitchInto(switchAnimation, winType);

      // Final counter segment — runs across SWITCH and IDLE.
      if (winType !== "max_win") {
        this.startTierCounter(true);
      }

      const inTimeout = new TickerTimeout(() => {
        if (this.isPlaying) {
          if (this.winSpine) this.winSpine.autoUpdate = true;
          this.winSpine.state.setAnimation(0, idleAnimation, true);
          this.playWinPhaseSound(this.finalWinType, "idle");

          this.isWaitingForClick = true;
          this.currentAnimationPhase = "waiting-for-click";
          this.phaseStartTime = performance.now();
          this.phaseProgress = 0;
          resolve();
        }
      }, switchDuration);

      this.currentTimeouts.push(inTimeout);
    } else if (idleAnimation) {
      // Fallback: no switch animation — hard-swap the skin and idle.
      this.setSkin(winType);
      if (this.winSpine) this.winSpine.autoUpdate = true;
      this.winSpine.state.setAnimation(0, idleAnimation, true);
      if (winType !== "max_win") {
        this.startTierCounter(true);
      }
      this.isWaitingForClick = true;
      this.currentAnimationPhase = "waiting-for-click";
      this.phaseStartTime = performance.now();
      this.phaseProgress = 0;
      resolve();
    } else {
      this.finishAnimation();
      resolve();
    }
  }

  /**
   * Set spine skin for the given win tier.
   * Skins control which visual elements are shown (big_win, mega_win, etc.)
   * Animations are shared: in, idle, out.
   *
   * @param {string} winType - Win type matching a spine skin name (e.g. "big_win", "mega_win")
   */
  setSkin(winType) {
    if (!this.winSpine?.skeleton) return;

    try {
      const skinData = this.winSpine.skeleton.data.findSkin(winType);
      if (skinData) {
        this.winSpine.skeleton.setSkin(skinData);
        // Slots only. setToSetupPose() used to follow this line and was the
        // cause of the flash on every skin swap: it resets BONES as well, so
        // the skeleton snapped to its rest pose for one frame until the next
        // tick re-applied the animation. Invisible on big -> mega, obvious on
        // mega -> supermega, whose rest pose is a much bigger jump — 55 slots
        // against 38.
        this.winSpine.skeleton.setSlotsToSetupPose();

        // Re-apply the animation over the fresh skin in the SAME frame, so
        // nothing can render between the swap and the next tick.
        if (this.winSpine.state) {
          this.winSpine.state.apply(this.winSpine.skeleton);
          this.winSpine.skeleton.updateWorldTransform();
        }

        // setSlotsToSetupPose restores slot attachments to the skin's
        // defaults. When the counter is detached, clear the value/multiplier
        // slots so the baked-in placeholder does not reappear.
        if (this.counterDetached) {
          const valueSlot = this.winSpine.skeleton.findSlot("txt_slot_value");
          if (valueSlot) valueSlot.setAttachment(null);
          const multSlotName = this._findMultiplierSlotName();
          const multSlot = multSlotName
            ? this.winSpine.skeleton.findSlot(multSlotName)
            : null;
          if (multSlot) multSlot.setAttachment(null);
        }
      }
    } catch (error) {
      // Silent fail — skin not found, keep current
    }
  }

  /**
   * Find animation names — in the secondary spine, animations are shared
   * across all tiers: "in", "idle", "out". Skin determines the visual.
   */
  findAnimations() {
    const animations = this.getAvailableAnimations();
    const inAnimation = animations.includes("in") ? "in" : null;
    const idleAnimation = animations.includes("idle") ? "idle" : null;
    const outAnimation = animations.includes("out") ? "out" : null;
    // The tier-to-tier transition. It is authored to read as an OUT for the
    // outgoing tier and an IN for the incoming one, with a `switch` event at
    // its exact midpoint where the skin is swapped. See _playSwitchInto.
    const switchAnimation = animations.includes("switch") ? "switch" : null;

    return { inAnimation, idleAnimation, outAnimation, switchAnimation };
  }

  /**
   * Play the transition into the next tier.
   *
   * The whole point of the `switch` animation: the outgoing tier melts away
   * over the first half, the skin is swapped at the midpoint, and the incoming
   * tier builds over the second half. One animation, no second IN, and no
   * visible reset between tiers.
   *
   * The swap is driven by the spine's own `switch` event rather than a timer,
   * so it stays locked to the artwork if the animation is ever retimed. A
   * midpoint fallback covers a re-export that loses the event — without it a
   * missing event would leave the tier showing the WRONG skin for good.
   *
   * @param {string} switchAnimation - resolved animation name
   * @param {string} winType - the tier being switched INTO
   * @returns {number} the switch duration in ms
   */
  _playSwitchInto(switchAnimation, winType) {
    const duration = this.getSpineAnimationDuration("switch");
    let swapped = false;

    const swap = () => {
      if (swapped) return;
      swapped = true;
      this.setSkin(winType);
    };

    const listener = {
      event: (entry, event) => {
        if (event?.data?.name !== "switch") return;
        this.winSpine.state.removeListener(listener);
        swap();
      },
    };
    this.winSpine.state.addListener(listener);
    if (this.winSpine) this.winSpine.autoUpdate = true;
    this.winSpine.state.setAnimation(0, switchAnimation, false);

    // Fallback only — fires at the midpoint if the event never arrives.
    const fallback = new TickerTimeout(() => {
      if (this.isPlaying) swap();
    }, duration / 2);
    this.currentTimeouts.push(fallback);

    return duration;
  }

  /**
   * Duration of a tier that is entered via `switch` rather than `in`.
   */
  getSwitchTierDuration() {
    return (
      this.getSpineAnimationDuration("switch") +
      this.getSpineAnimationDuration("idle")
    );
  }

  /**
   * Handle user click to skip counter or close animation
   *
   * Two-click logic:
   * 1. First click (during counter animation): Skips counter to final value
   * 2. Second click (after counter finished): Closes the entire animation
   *
   * Also handles spacebar input and prevents double-clicks with debouncing.
   *
   * @returns {boolean} True if click was handled, false if ignored
   */
  handleUserClick() {
    // Basic validation
    if (!this.isPlaying || !this.winSpine || !this.winSpine.state) {
      return false;
    }

    // Block interactions during closing or finishing phases
    if (this.isClosing || this.isFinishing) {
      return false;
    }

    // Skip protection: block input for first second of animation
    if (!this.canSkipAnimation) {
      return false;
    }

    // CRITICAL: If debounce timeout exists, block all clicks (prevents timeout buildup)
    // This ensures only ONE click is processed per 300ms window
    if (this.debounceTimeout) {
      return false;
    }

    // FIRST CLICK ONLY: Create debounce timeout to block subsequent clicks for 300ms
    // This timeout will be the ONLY timeout created, no matter how many clicks
    this.debounceTimeout = new TickerTimeout(() => {
      this.debounceTimeout = null;
    }, 300);

    // Check if counter is currently animating
    const isCounterAnimating = this.isCounterAnimating();
    const currentCounterValue = this.getCurrentCounterValue();
    const isCounterFinished = currentCounterValue >= this.totalWinAmount;

    // SIMPLE LOGIC: If counter is animating, skip it; if finished, close animation
    if (isCounterAnimating && !isCounterFinished) {
      // Counter is still animating - skip to final
      this.hasHandledSkipClick = true;

      // Jump counter to final value immediately
      this.jumpCounterToValue(this.totalWinAmount);

      this.counterJumpedToFinal = true;

      // Counter skipped to final - signal WinDisplay to show win text
      if (this.dispatch) {
        this.dispatch(setWinAnimationCounterFinished(true));
      }

      // Skip to final animation (handles multiplier/CTA and auto-close internally)
      this.skipToFinalAnimation();

      return true;
    } else {
      // Counter is finished or not animating - close animation
      this.hasHandledCloseClick = true;

      // Clear auto-close timer since user clicked manually
      if (this.autoCloseTimeout) {
        this.autoCloseTimeout.clear();
        this.autoCloseTimeout = null;
      }

      this.playClosingAnimation();

      return true;
    }
  }

  /**
   * Start auto-close timer for animations
   *
   * Automatically closes win animations if user doesn't interact after counter
   * reaches final value. Prevents animations from staying open indefinitely.
   * Timer is cleared if user clicks manually.
   */
  startAutoCloseTimer() {
    // Clear any existing auto-close timer
    if (this.autoCloseTimeout) {
      this.autoCloseTimeout.clear();
    }

    // Use MAX_WIN auto-close delay for max_win (30s), normal delay for other wins (3s)
    const autoCloseDelay =
      this.finalWinType === "max_win"
        ? FEATURES_CONFIG.MAX_WIN.AUTO_CLOSE_DELAY
        : FEATURES_CONFIG.WIN_CELEBRATION.AUTO_CLOSE_DELAY;

    // Start auto-close timer using configured delay
    this.autoCloseTimeout = new TickerTimeout(() => {
      if (this.isPlaying && !this.hasHandledCloseClick) {
        this.hasHandledCloseClick = true;
        this.playClosingAnimation();
      }
    }, autoCloseDelay);
  }

  /**
   * Skip all intermediate animations and jump straight to final animation
   *
   * When user clicks skip during counter animation:
   * - Stops all current animations and timeouts
   * - Jumps counter to final value instantly
   * - Plays final win type animation (EPIC for highest wins)
   * - If already playing final animation, handles smooth transition
   *
   * Used for progressive wins to skip BIG→SUPER→MEGA and go straight to EPIC.
   */
  skipToFinalAnimation() {
    const finalWinType = this.animationQueue[this.animationQueue.length - 1];
    const isAlreadyAtFinalAnimation =
      this.currentAnimationIndex === this.animationQueue.length - 1;

    // If we're already playing the final animation, handle it smoothly
    if (isAlreadyAtFinalAnimation && this.isPlaying) {
      this.handleSkipDuringFinalAnimation(finalWinType);
      return;
    }

    // Clear all timeouts and animations to stop intermediate animations
    this.clearAllResources();

    // Stop current banner sounds only (keep win_music playing - it plays throughout entire sequence)
    this.stopBannerSounds();

    // No music switch on the skip either — see the note on the live path. The
    // skip must leave win_music exactly as the unskipped sequence does, or the
    // two ways of reaching MAX WIN would sound different.

    // Jump to final animation index
    this.currentAnimationIndex = this.animationQueue.length - 1;

    // Jump counter to final value immediately when skipping (only if not already jumped)
    if (!this.counterJumpedToFinal) {
      this.jumpCounterToValue(this.totalWinAmount);
      this.counterJumpedToFinal = true;
      // Signal WinDisplay to show win text
      if (this.dispatch) {
        this.dispatch(setWinAnimationCounterFinished(true));
      }
    }

    // GDD §17.2: Always show multiplier/CTA when reaching final animation
    // (regardless of whether handleUserClick or skipToFinalAnimation jumped the counter)
    if (finalWinType === "max_win") {
      this.showTapToContinue();
    } else {
      this.showMultiplier();
      this.startAutoCloseTimer();
    }

    // Show spine if we're coming from counter-only phase
    if (!this.isVisible) {
      // Same pairing as showAndStartAnimation: shown by alpha, so woken here.
      this.winSpine.autoUpdate = true;
      this.winSpine.alpha = 1; // Show (use alpha not visible)
      this.isVisible = true;
    }

    // Play banner sound for the final win type when skipping
    this.playWinPhaseSound(finalWinType, "in");

    // ── The number stays put while the final banner arrives ──
    //
    // `in` keys this slot's rgba from 00 to ff, so an attached counter fades out
    // and back in over a skip. Detached, it holds its value at full alpha and the
    // art enters behind it. Reattached at the end of `in`, where the bone's own
    // scale pulse takes over with the idle.
    this.detachCounterFromSlot();

    // Swap animation — don't clearTracks() as it flashes the setup pose for one frame
    // setAnimation() below replaces the current animation, mix durations handle the blend
    this.winSpine.state.clearListeners();

    // Switch skin to the final tier
    this.setSkin(finalWinType);

    const { inAnimation, idleAnimation } = this.findAnimations();

    // ── A skip drops the SWITCH, and nothing else ──
    //
    // `switch` is the transition between two tiers, and a skip has no interest in
    // the tiers it is passing over — so it is never played here. `in` is a
    // different thing entirely: it is how the final tier ARRIVES, and the player
    // has to see the banner they skipped to arrive. Without it the top tier is
    // simply on screen out of nowhere.
    //
    // It was briefly dropped as well, on the reasoning that a banner was already
    // showing so nothing needed to enter. Wrong: what enters is the FINAL tier,
    // which the player has not seen yet.
    //
    // The xBet is safe across it now for a different reason — `in` keys that
    // slot from alpha 0 to 1, and the reveal gate waits for the slot's own alpha
    // rather than racing the clip. See _pollMultiplierGate.
    if (inAnimation && idleAnimation) {
      if (this.winSpine) this.winSpine.autoUpdate = true;
      const inEntry = this.winSpine.state.setAnimation(0, inAnimation, false);

      // ── Faster, because this one was ASKED for ──
      //
      // The arrival stays; only its speed changes. See SKIP_IN_TIME_SCALE for
      // why this branch was a second slower than the other skip path and why
      // shortening the clip is the fix rather than removing it.
      //
      // On the ENTRY, not on the state: the idle queued behind it is a new
      // TrackEntry and defaults to timeScale 1, so the loop the player is left
      // watching runs at normal speed without anything resetting it.
      if (inEntry) inEntry.timeScale = SKIP_IN_TIME_SCALE;

      // After IN completes, loop IDLE. Divided by the same factor — the clip
      // is the same length, it is being played faster, so the wall-clock wait
      // is shorter and a timer still keyed to the full duration would hold the
      // idle (and the xBet with it) exactly as long as before.
      const inDuration =
        this.getSpineAnimationDuration("in") / SKIP_IN_TIME_SCALE;

      const inTimeout = new TickerTimeout(() => {
        if (this.isPlaying) {
          // Reattach counter to slot so it pulses with the final IDLE
          this.reattachCounterToSlot();

          if (this.winSpine) this.winSpine.autoUpdate = true;
          this.winSpine.state.addAnimation(0, idleAnimation, true, 0);
          // The `in` clip has ended and the idle is queued behind it — the
          // held xBet belongs here. Without this, a skip that lands mid-switch
          // queues the reveal and this branch never releases it.
          this.playWinPhaseSound(this.finalWinType, "idle");
          this.isWaitingForClick = true;
          this.currentAnimationPhase = "waiting-for-click";
          this.phaseStartTime = performance.now();
          this.phaseProgress = 0;
        }
      }, inDuration);
      this.currentTimeouts.push(inTimeout);
    } else if (idleAnimation) {
      // Reattach counter to slot so it pulses with the final IDLE
      this.reattachCounterToSlot();
      if (this.winSpine) this.winSpine.autoUpdate = true;
      this.winSpine.state.setAnimation(0, idleAnimation, true);
      this.isWaitingForClick = true;
      this.currentAnimationPhase = "waiting-for-click";
      // The final tier is simply ON now — either the skeleton has no `in`, or a
      // banner was already showing and we deliberately did not replay one (see
      // the branch above). Either way there is nothing to wait for.
      // GDD §17.2: CTA button only on Max Win, multiplier only for BW/MW/SMW
      if (finalWinType === "max_win") {
        this.showTapToContinue();
      } else {
        this.showMultiplier();
        this.startAutoCloseTimer();
      }
      this.phaseStartTime = performance.now();
      this.phaseProgress = 0;
    }
  }

  /**
   * Handle skip during final animation
   * Checks current phase and transitions smoothly to idle/out
   */
  handleSkipDuringFinalAnimation(finalWinType) {
    const { idleAnimation } = this.findAnimations();

    // Get current animation being played
    const currentAnimation = this.winSpine.state.tracks[0]?.animation?.name;

    // Check what phase we're currently in (generic names — skin determines visual)
    const inAnimationName = "in";
    const idleAnimationName = "idle";

    // GDD §17.2: Counter was jumped by handleUserClick — show multiplier now
    if (finalWinType === "max_win") {
      this.showTapToContinue();
    } else {
      this.showMultiplier();
    }
    this.startAutoCloseTimer();

    if (currentAnimation === inAnimationName) {
      // We're in the IN phase - smoothly transition to IDLE
      this.winSpine.state.clearListeners();

      if (idleAnimation) {
        // Reattach counter to slot so it pulses with the final IDLE
        this.reattachCounterToSlot();

        // Use addAnimation for smooth blend from IN to IDLE
        if (this.winSpine) this.winSpine.autoUpdate = true;
        this.winSpine.state.addAnimation(0, idleAnimation, true, 0);

        // Set waiting state
        this.isWaitingForClick = true;
        this.currentAnimationPhase = "waiting-for-click";
        this.phaseStartTime = performance.now();
        this.phaseProgress = 0;
      }
    } else if (currentAnimation === idleAnimationName) {
      // We're already in IDLE phase - stay and wait for user
      this.isWaitingForClick = true;
      this.currentAnimationPhase = "waiting-for-click";
      this.phaseStartTime = performance.now();
      this.phaseProgress = 0;
    } else {
      // Fallback: assume we're in IDLE and stay there
      this.isWaitingForClick = true;
      this.currentAnimationPhase = "waiting-for-click";
      this.phaseStartTime = performance.now();
      this.phaseProgress = 0;
    }
  }

  /**
   * Play closing out animation when user clicks to continue
   */
  playClosingAnimation() {
    if (!this.isWaitingForClick || !this.winSpine?.state) return;

    this.isClosing = true;
    this.isWaitingForClick = false;

    // ── The celebration's own closing sound ──
    //
    // Here and nowhere else, because this is the single gate every close
    // passes through: the tap, the spacebar (which routes to the same handler)
    // and the auto-close timer all arrive at this method, and the
    // isWaitingForClick guard above means it runs exactly once.
    //
    // It REPLACES tap_to_continue on this screen. The dismiss handler in
    // WinAnimationSecondary used to play that, which also fired on the FIRST
    // tap — the one that only jumps the counter and does not close anything.
    // This screen now has one sound, on the one event it belongs to.
    soundManager.play(SOUND_NAME.win_animation_end);


    // ── The music crosses over the CLOSING ANIMATION, not before it ──
    //
    // Measured off the `out` clip so the win music lands exactly as the spine
    // does, with a floor for the no-`out` path. The background is faded IN on
    // the same window, right here rather than in finishAnimation — started
    // there it began only once the close had already finished, so the whole
    // close played in silence and the base track arrived after it.
    // Resolved here rather than below, because the fade length is measured off
    // it — the rig does not always call the clip "out", and measuring the
    // literal name silently fell back to the helper's 1000ms default.
    const { outAnimation } = this.findAnimations();
    const outAnim = outAnimation || "out";
    const outFadeMs = Math.max(
      WIN_MUSIC_OUTRO_FADE_MIN_MS,
      this.getSpineAnimationDuration(outAnim),
    );

    // Crossfade: fade out win loop music + idle phase sound, then play out sound
    try {
      soundManager.fadeOut(SOUND_NAME.win_music, outFadeMs);
      // The other half of the same crossfade.
      this.resumeBackgroundMusic(outFadeMs);

      // Fade out the idle phase sound so it blends with the out sound
      if (this.finalWinType) {
        const idleSoundKey = `${this.finalWinType}_idle`;
        if (SOUND_NAME[idleSoundKey]) {
          soundManager.fadeOut(SOUND_NAME[idleSoundKey], 500);
        }
      }
    } catch (error) {
      // Failed to handle music transition
    }

    // Counter is already reattached to slot (done when final IDLE started)
    // If still detached for any reason (e.g. skip), reattach now as safety net
    this.reattachCounterToSlot();

    this.winSpine.state.clearListeners();

    if (outAnim) {
      // Use setAnimation — the mix duration configured in initializeWinSpine
      // (stateData.setMix) handles the crossfade automatically
      if (this.winSpine) this.winSpine.autoUpdate = true;
      this.winSpine.state.setAnimation(0, outAnim, false);
      this.playWinPhaseSound(this.finalWinType, "out");
      this.winSpine.state.addListener({
        complete: (e) => {
          if (e.animation.name === outAnim && this.isPlaying) {
            this.finishAnimation();
          }
        },
      });
    } else {
      this.finishAnimation();
    }
  }

  /**
   * Complete the animation sequence and perform full cleanup
   *
   * Called when user closes animation or auto-close timer expires.
   * Performs final cleanup: stops sounds, hides UI, clears resources,
   * dispatches Redux state changes, and resets all animation state.
   * This is the final method called when win celebration ends.
   */
  finishAnimation() {
    if (!this.winSpine || !this.initialized) {
      return;
    }

    // Set finishing flag immediately to prevent retriggering
    this.isFinishing = true;



    // Dispatch Redux state change immediately before any async operations
    if (this.dispatch) {
      this.dispatch(setWinAnimationsPlaying(false));
      this.dispatch(setWinAnimationCounterFinished(false));
      // Unblock symbol popup when win animations finish
      this.dispatch(setSymbolPopupBlocked(false));

      // Skip balance update when this is an H&W trigger round — the coin animations
      // are about to play and a balance update would cause jitter.
      // SceneTransitionOverlay already handles balance when returning to base game.
      const featureState = store.getState()?.game?.featureState;
      const isHoldAndWinTrigger =
        featureState?.type === "holdAndWin" && featureState?.phase === "intro";

      // Skip balance update during free spins — balance is synced once at the end
      // of the feature, on the way back through the wipe.
      //
      // featureGame.freeSpinActive is ZEUS's flag and nothing in Mad Labs sets
      // it, so this skip was dead: an animated win inside the bonus synced the
      // balance mid-feature, which is exactly what every bonus here is supposed
      // not to do. freeSpins.active is this game's equivalent.
      const isFreeSpinActive = store.getState()?.freeSpins?.active;

      // Skip balance update during free rounds — balance doesn't change mid-FR
      const isFreeRoundsActive = store.getState()?.freeRounds?.status === "active";

      // Provider-specific balance update strategy:
      // PopiPlay: Call refreshGameData() to sync with server (server tracks all transactions)
      // Redstone: Call updateDisplayedBalance() to sync displayed balance with Redux balance (already correct from spin)
      if (!isHoldAndWinTrigger && !isFreeSpinActive && !isFreeRoundsActive) {
        const replayState = store.getState()?.replay;
        if (replayState?.sessionActive) {
          // Replay (both entry points): never touch the live wallet
          // (syncGameState hits the real server — which the standalone
          // replay link doesn't even have, window.__OPTIONS__.api is empty
          // there — and updateDisplayedBalance reads the real Redux
          // balance). Add this step's win to the replay-only display
          // balance instead, at the exact same "win just finished" moment
          // the live path would sync. replaySlice.fetchReplayInit primes
          // replayBalance at the round's pre-win amount on every
          // playthrough, so this recreates the original reveal each time.
          this.dispatch(applyReplayWin(this.totalWinAmount || 0));
        } else if (handleShouldSyncBalanceOnWinClose()) {
          this.dispatch(syncGameState({ syncBalance: true }));
        } else {
          this.dispatch(updateDisplayedBalance());
        }
      }
    }

    // ── A FADE, never a stop ──
    //
    // This was `soundManager.stop(win_music)`, and it is the reason a skipped
    // celebration ended in silence: playClosingAnimation had just started the
    // music's tail, and this ran the moment the `out` clip completed and cut it
    // dead. A fade here cannot do that — fadeOut early-returns on a sound that
    // is no longer playing, so the normal path is untouched and the only thing
    // this still catches is a close that never went through the crossfade.
    try {
      if (SOUND_NAME.win_music) {
        soundManager.fadeOut(SOUND_NAME.win_music, WIN_MUSIC_OUTRO_FADE_MIN_MS);
      }
    } catch (error) {
      // Silent fail
    }

    // Safety net only. The crossfade normally happens in playClosingAnimation,
    // and the flag it clears makes this a no-op — this covers the paths that
    // finish without closing.
    this.resumeBackgroundMusic();

    // Clear all resources to prevent buildup
    this.clearAllResources();

    // Reset playing states
    this.isPlaying = false;
    this.isWaitingForClick = false;

    // Disable scaling calculations when animation finishes
    this.shouldBeActive = false;

    // Reset phase tracking
    this.currentAnimationPhase = null;
    this.phaseStartTime = 0;
    this.phaseProgress = 0;

    // Counter and button don't need manual hiding — they're children of spine
    // and the spine's alpha change will automatically hide them



    // Hide spine immediately when finishAnimation starts to prevent visibility issues
    if (this.winSpine) {
      this.winSpine.alpha = 0; // Hide (use alpha not visible)
      // ── And STOP SOLVING it ──
      //
      // `alpha = 0` takes it out of the render. It does nothing about
      // `autoUpdate`, which is a separate subscription on Ticker.shared:
      // spine-pixi runs `state.update`, `state.apply` and
      // `skeleton.updateWorldTransform` from it on every frame, reading none of
      // `visible`, `renderable` or `worldAlpha`.
      //
      // This rig is 244 bones and 248 slots — the largest in the game by a
      // wide margin. Before this, the only place it was ever put back to sleep
      // was `initializeSpineStructure`, which runs ONCE at boot. Sixteen wake
      // points and no sleep meant that from the player's FIRST WIN onward it
      // solved its whole hierarchy every frame for the rest of the session,
      // base game and bonus room alike.
      //
      // Safe because every path that shows it again goes through a
      // `setAnimation`, and all sixteen of those already raise `autoUpdate`.
      this.winSpine.autoUpdate = false;
    }
    this.isVisible = false;

    // Fade out background with callback to clean up state after animation completes
    this.overlayRef.current?.fadeOutBackground(() => {
      // Stop banner sounds when overlay fades out completely
      this.stopBannerSounds();

      // Double-check spine is still hidden (defensive programming)
      if (this.winSpine) {
        this.winSpine.alpha = 0; // Hide (use alpha not visible)
        // Asleep too — see the note at the top of finishAnimation.
        this.winSpine.autoUpdate = false;
      }
      this.isVisible = false;
    });

    // Reset animation queue and stored values
    this.animationQueue = [];
    this.currentAnimationIndex = 0;
    this.finalWinType = null;
    this.totalWinAmount = null;
    this.betAmount = null;
    this.lastWinAmount = null; // Reset to allow new win animations
    this._multiplierRequest = null;
    this._disarmMultiplierGate();
    this.counterJumpedToFinal = false;
    // "XX X BET" is already on screen for this celebration — the latch
    // of the one place that draws it. See _fadeInMultiplier.
    this._multiplierRevealed = false;
    this.counterDetached = false;

    // Reset user interaction guards
    this.hasHandledSkipClick = false;
    this.hasHandledCloseClick = false;
    this.isHandlingUserClick = false;
    this.canSkipAnimation = false;
    this.isClosing = false; // Reset closing state

    // Reset spine timeScale in case it was paused
    if (this.winSpine && this.winSpine.state) {
      this.winSpine.state.timeScale = 1;
    }

    // Reset auto-close timeout
    this.autoCloseTimeout = null;

    // Reset finishing flag after all cleanup is complete
    this.isFinishing = false;
  }

  /**
   * Immediately stop all win animations and perform emergency cleanup
   *
   * Used when component unmounts or critical errors occur.
   * Unlike finishAnimation(), this doesn't dispatch Redux state changes
   * or perform full UI transitions - just stops everything immediately.
   */
  stopWinAnimation() {


    // Clear all resources (timeouts + animations)
    this.clearAllResources();

    // Stop all win sounds
    this.stopAllWinSounds();

    // Resume background music if it was playing before
    this.resumeBackgroundMusic();

    if (this.winSpine && this.isPlaying) {
      this.winSpine.state?.clearListeners();
      this.winSpine.state?.clearTracks();
      this.winSpine.alpha = 0; // Hide (use alpha not visible)
      // The tracks are cleared just above, so there is nothing left to solve.
      // See the note at the top of finishAnimation.
      this.winSpine.autoUpdate = false;
      this.isPlaying = false;
      this.isVisible = false;

      // Disable scaling calculations when animation is stopped
      this.shouldBeActive = false;



      this.overlayRef.current?.fadeOutBackground();
    }

    // Reset user interaction guards
    this.hasHandledSkipClick = false;
    this.hasHandledCloseClick = false;
    this.isHandlingUserClick = false;
    this.canSkipAnimation = false;
    this.isClosing = false; // Reset closing state

    // Reset phase tracking
    this.currentAnimationPhase = null;
    this.phaseStartTime = 0;
    this.phaseProgress = 0;

    // Reset finishing flag if animation was stopped
    this.isFinishing = false;
  }

  /**
   * Cleanup resources
   */
  cleanup() {


    // Clear all resources before cleanup
    this.clearAllResources();
    this._multiplierRequest = null;
    this._disarmMultiplierGate();

    // Cleanup counter
    if (this.counterContainer) {
      this.counterContainer.destroy({ children: true });
      this.counterContainer = null;
      this._charTexts = [];
    }

    // Cleanup multiplier
    if (this.multiplierContainer) {
      this.multiplierContainer.destroy({ children: true });
      this.multiplierContainer = null;
      this.multiplierText = null;
    }

    // Cleanup button
    if (this.buttonContainer) {
      this.buttonContainer.destroy({ children: true });
      this.buttonContainer = null;
      this.buttonText = null;
    }

    // Disable scaling calculations when animation is no longer active
    this.shouldBeActive = false;

    if (this.winSpine) {
      // Defensive cleanup - ensure spine state is properly cleared
      if (this.winSpine.state) {
        try {
          this.winSpine.state.clearListeners();
          this.winSpine.state.clearTracks();
          this.winSpine.state.timeScale = 1.0;
        } catch (error) {
          // Spine might already be destroyed - continue cleanup
        }
      }

      // Remove from container safely
      if (this.container && this.container.children.includes(this.winSpine)) {
        this.container.removeChild(this.winSpine);
      }

      // Destroy spine instance
      try {
        this.winSpine.destroy();
      } catch (error) {
        // Spine might already be destroyed - continue cleanup
      }

      this.winSpine = null;
    }

    this.initialized = false;
  }
}

export default WinAnimationSecondaryController;
